export const sanityBlogOverviewDocumentId = "blogOverview";
export const sanityCaseStudiesOverviewDocumentId = "caseStudiesOverview";
export const sanityWebinarsOverviewDocumentId = "webinarsOverview";
export const sanityRoutableDocumentTypes = ["page", "hardware", "software", "industry", "article", "caseStudy", "event", "webinar", "download"] as const;
export const sanityReferenceDocumentTypes = [...sanityRoutableDocumentTypes, "partner", "distributor", "office", "author", "articleCategory", "articleLegacyTag", "country", "region", "service", "industryCategory", "productGeneration"] as const;
export const sanitySingletonDocuments = [{
  id: "homepage",
  title: "Homepage",
  schemaType: "page",
  uri: "/"
}, {
  id: "navigationSettings",
  title: "Navigation",
  schemaType: "navigationSettings"
}, {
  id: "footerSettings",
  title: "Footer",
  schemaType: "footerSettings"
}, {
  id: "seoSettings",
  title: "SEO defaults",
  schemaType: "seoSettings"
}, {
  id: "announcementSettings",
  title: "Announcement bar",
  schemaType: "announcementSettings"
}, {
  id: "redirectSettings",
  title: "Redirects",
  schemaType: "redirectSettings"
},
// Curated index served at /llms.txt, pointing AI crawlers at the pages worth
// reading first. Deliberately not a sitemap clone; see features/app/llms.ts.
{
  id: "llmsSettings",
  title: "llms.txt",
  schemaType: "llmsSettings"
},
// Generic quote singleton retained so existing content stays editable.
{
  id: "requestQuoteSettings",
  title: "Request Quote (legacy)",
  schemaType: "requestQuoteSettings"
}, {
  id: "requestQuoteRev7Settings",
  title: "Request Quote Rev7",
  schemaType: "requestQuoteSettings"
}, {
  id: "requestQuoteRev8Settings",
  title: "Request Quote Rev8",
  schemaType: "requestQuoteSettings"
}, {
  id: "requestDemoBluecitySettings",
  title: "Request Demo Bluecity",
  schemaType: "requestDemoSettings"
}, {
  id: "requestDemoGeminiSettings",
  title: "Request Demo Gemini",
  schemaType: "requestDemoSettings"
},
// Administrator-only cache settings singleton (masked purge secret +
// cache endpoint URLs). Clears are triggered from the administrator-only
// Cache Dashboard; the singleton's Studio visibility is gated to the
// `administrator` role in the desk structure.
{
  id: "cacheSettings",
  title: "Cache settings",
  schemaType: "cacheSettings"
}] as const;
export type SanityRoutableDocumentType = (typeof sanityRoutableDocumentTypes)[number];
export type SanityReferenceDocumentType = (typeof sanityReferenceDocumentTypes)[number];
export type SanitySingletonDocument = (typeof sanitySingletonDocuments)[number];
export const sanityRouteDocumentTypeFilter = sanityRoutableDocumentTypes.map(type => `"${type}"`).join(",");
export type SanityWildLinkKind = "internal" | "external" | "email" | "phone" | "file";
export type SanityResolvedInternalLink = {
  _id?: string;
  _type?: SanityReferenceDocumentType;
  title?: string;
  uri?: string;
  slug?: string;
};
export type SanityReferenceSummary = {
  _id?: string;
  _type?: SanityReferenceDocumentType;
  title?: string;
  uri?: string;
  slug?: string;
};
export type SanityOfficeLocation = {
  _key?: string;
  _type?: "officeLocation";
  address?: string;
  lat?: number;
  lng?: number;
};
export type SanityOfficeDocument = {
  _id?: string;
  _type: "office";
  title?: string;
  slug?: string;
  logo?: SanityMediaValue;
  locations?: SanityOfficeLocation[];
};
export type SanityPartnerDocument = {
  _id?: string;
  _type: "partner";
  title?: string;
  slug?: string;
  url?: string;
  logo?: SanityMediaValue;
  intro?: SanityRichTextValue;
  regions?: SanityReferenceSummary[];
  countries?: SanityReferenceSummary[];
  services?: SanityReferenceSummary[];
  industry?: SanityReferenceSummary;
};
export type SanityDistributorLocation = {
  _key?: string;
  _type?: "distributorLocation";
  country?: string;
  state?: string;
  lat?: number;
  lng?: number;
};
export type SanityDistributorDocument = {
  _id?: string;
  _type: "distributor";
  title?: string;
  slug?: string;
  url?: string;
  email?: string;
  logo?: SanityMediaValue;
  intro?: SanityRichTextValue;
  industry?: SanityReferenceSummary;
  regions?: SanityReferenceSummary[];
  locations?: SanityDistributorLocation[];
};
export type SanityWildLinkValue = {
  _type?: "wild.link";
  kind?: SanityWildLinkKind;
  customText?: string;
  canDownload?: boolean;
  download?: boolean;
  openInNewTab?: boolean;
  newWindow?: boolean;
  href?: string;
  text?: string;
  external?: string;
  email?: string;
  phone?: string;
  file?: {
    asset?: {
      _id?: string;
      url?: string;
      originalFilename?: string;
      mimeType?: string;
      size?: number;
    };
  };
  internal?: {
    sectionTarget?: string;
    link?: SanityResolvedInternalLink;
  };
};
export type SanityLinkValue = SanityWildLinkValue;
export type SanityNavTheme = "solid" | "transparent";
export type SanityPattern = "dotted" | "grid" | "none";
export type SanityPageMode = "default" | "landing";
/** Page-level design system version. "3" renders components that have a 3.0 sibling; missing means "2". */
export type SanityDesignVersion = "2" | "3";
export type SanitySubNavMode = "light" | "dark";
export type SanitySupportLinkVariant = "primary" | "secondary" | "underline";
export type SanityMediaDimensions = {
  aspectRatio?: number | null;
  height?: number | null;
  width?: number | null;
};

// Editorial crop/hotspot as stored by the Studio image field (`options.hotspot`).
// All values are 0..1 fractions of the original asset. The crop insets each edge;
// the hotspot is a box expressed as a center point plus width/height.
export type SanityImageCrop = {
  _type?: "sanity.imageCrop";
  bottom: number;
  left: number;
  right: number;
  top: number;
};
export type SanityImageHotspot = {
  _type?: "sanity.imageHotspot";
  height: number;
  width: number;
  x: number;
  y: number;
};
export type SanityImageValue = {
  _id?: string;
  altText?: string | null;
  crop?: SanityImageCrop | null;
  description?: string | null;
  dimensions?: SanityMediaDimensions | null;
  hotspot?: SanityImageHotspot | null;
  lqip?: string | null;
  mimeType?: string | null;
  originalFilename?: string | null;
  provider?: SanityMediaProvider | null;
  title?: string | null;
  url?: string | null;
};
export type SanityMediaProvider = "air" | "sanity";
export type SanityMediaDelivery = "file" | "hls";
export type SanityMediaSource = {
  delivery?: SanityMediaDelivery | null;
  dimensions?: SanityMediaDimensions | null;
  duration?: number | null;
  mimeType?: string | null;
  provider?: SanityMediaProvider | null;
  src?: string | null;
};
export type SanityVideoValue = {
  cover?: SanityImageValue | null;
  kind?: "air" | "file" | "mux" | "vimeo" | "youtube";
  options?: Record<string, unknown> | null;
  source?: SanityMediaSource | null;
};
export type SanityMotionValue = {
  kind?: "lottie" | "rive";
  options?: Record<string, unknown> | null;
  source?: SanityMediaSource | null;
};
export type SanityMediaValue = {
  _type?: "wild.media";
  aspectRatio?: string | null;
  caption?: string | null;
  image?: {
    source?: SanityImageValue | null;
  } | null;
  isDarkened?: boolean | null;
  kind?: "image" | "motion" | "video";
  motion?: SanityMotionValue | null;
  video?: SanityVideoValue | null;
};
export type SanityRichTextValue = unknown[];
function getUrlVideoId(value: string, provider: "vimeo" | "youtube"): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  try {
    const url = new URL(trimmed);
    const hostname = url.hostname.toLowerCase();
    const pathParts = url.pathname.split("/").filter(Boolean);
    if (provider === "youtube") {
      const isShortHost = hostname === "youtu.be";
      const isYouTubeHost = hostname === "youtube.com" || hostname.endsWith(".youtube.com") || hostname === "youtube-nocookie.com" || hostname.endsWith(".youtube-nocookie.com");
      if (!isShortHost && !isYouTubeHost) {
        return "";
      }
      if (isShortHost) {
        return pathParts[0] || "";
      }
      const videoIds = url.searchParams.getAll("v").filter(Boolean);
      let paramId = videoIds[0];
      for (let index = videoIds.length - 1; index >= 0; index -= 1) {
        const id = videoIds[index];
        if (id && !id.includes("youtube.com") && !id.includes("youtu.be")) {
          paramId = id;
          break;
        }
      }
      if (paramId) {
        return getUrlVideoId(paramId, "youtube");
      }
      const embedIndex = pathParts.findIndex(part => part === "embed" || part === "shorts" || part === "live" || part === "v");
      return embedIndex >= 0 ? pathParts[embedIndex + 1] || "" : "";
    }
    return pathParts.find(part => /^\d+$/.test(part)) || "";
  } catch {
    return trimmed;
  }
}
export function getSanityVideoEmbedId(value: string, provider: "mux" | "vimeo" | "youtube"): string {
  if (provider === "mux") {
    return value.trim();
  }
  return getUrlVideoId(value, provider);
}
export type SanitySeoImage = {
  asset?: SanityImageValue;
};
export type SanitySeoMetadata = {
  noIndex?: boolean;
  title?: string;
  description?: string;
  image?: SanitySeoImage;
};
export type SanityNavigationLink = {
  _key?: string;
  _type: "navigationLink";
  link?: SanityLinkValue;
  label?: string;
};
export type SanityNavigationDrawerLink = {
  _key?: string;
  _type: "navigationDrawerLink";
  link?: SanityLinkValue;
  label?: string;
  size?: "default" | "small";
};
export type SanityNavigationQuickLinks = {
  _key?: string;
  _type: "navigationQuickLinks";
  title?: string;
  links?: SanityNavigationLink[];
};
export type SanityNavigationProductGroup = {
  _key?: string;
  _type: "navigationProductGroup";
  title?: string;
  links?: Array<SanityNavigationLink | SanityNavigationQuickLinks>;
};
export type SanityNavigationDropdown = {
  _key?: string;
  _type: "navigationDropdown";
  title?: string;
  items?: Array<SanityNavigationLink | SanityNavigationProductGroup | SanityNavigationQuickLinks>;
};
export type SanityNavigationAdditionalDropdown = {
  _key?: string;
  _type: "navigationAdditionalDropdown";
  title?: string;
  links?: SanityNavigationLink[];
};
export type SanityNavigationDrawerDropdown = {
  _key?: string;
  _type: "navigationDrawerDropdown";
  title?: string;
  links?: SanityNavigationDrawerLink[];
};
export type SanityFooterLink = {
  _key?: string;
  _type: "footerLink";
  link?: SanityLinkValue;
  label?: string;
};
export type SanityFooterGroup = {
  _key?: string;
  _type: "footerGroup";
  title?: string;
  links?: SanityFooterLink[];
};
export type SanityFooterSocial = {
  _key?: string;
  _type: "footerSocial";
  type?: "x" | "youtube" | "linkedin" | "facebook" | "instagram" | "github" | "tiktok";
  url?: string;
};

/**
 * Merged shape returned by `getSanitySiteSettings` — populated from the four
 * settings singletons (navigation, footer, seo, announcement) fetched in
 * parallel. Kept as a single type so layout adapters and consumers don't have
 * to know about the split.
 */
export type SanitySiteSettingsNavigation = {
  extraNavLogo?: SanityMediaValue;
  footerLink?: SanityLinkValue;
  extraNavLogoLink?: SanityLinkValue;
  extraNavLogoNegative?: SanityMediaValue;
  seoMetadata?: SanitySeoMetadata;
  navLinks?: Array<SanityNavigationLink | SanityNavigationDropdown | SanityNavigationQuickLinks>;
  navAdditionalLinks?: Array<SanityNavigationLink | SanityNavigationAdditionalDropdown>;
  navDrawerLinks?: Array<SanityNavigationDrawerLink | SanityNavigationDrawerDropdown>;
  navDrawerAdditionalLinks?: SanityNavigationLink[];
  footerLinks?: SanityFooterGroup[];
  footerAdditionalLinks?: SanityNavigationLink[];
  footerSocials?: SanityFooterSocial[];
  footerBottomLinks?: SanityNavigationLink[];
  newsletterText?: string;
  announcementBarText?: SanityRichTextValue;
  requestQuote?: SanityRequestQuoteGlobals | null;
};
export type SanityNavigationSettings = {
  extraNavLogo?: SanityMediaValue;
  extraNavLogoNegative?: SanityMediaValue;
  extraNavLogoLink?: SanityLinkValue;
  navLinks?: SanitySiteSettingsNavigation["navLinks"];
  navAdditionalLinks?: SanitySiteSettingsNavigation["navAdditionalLinks"];
  navDrawerLinks?: SanitySiteSettingsNavigation["navDrawerLinks"];
  navDrawerAdditionalLinks?: SanitySiteSettingsNavigation["navDrawerAdditionalLinks"];
};
export type SanityFooterSettings = {
  footerLink?: SanityLinkValue;
  footerLinks?: SanityFooterGroup[];
  footerAdditionalLinks?: SanityNavigationLink[];
  footerBottomLinks?: SanityNavigationLink[];
  footerSocials?: SanityFooterSocial[];
  newsletterText?: string;
};
export type SanitySeoSettings = {
  seoMetadata?: SanitySeoMetadata;
};
export type SanityAnnouncementSettings = {
  announcementBarText?: SanityRichTextValue;
};
export type SanityLlmsLink = {
  _key?: string;
  title?: string;
  /** Resolved `uri.current` of the referenced document; wins over `href`. */
  uri?: string;
  /** Escape hatch for pages this CMS does not own (external docs, spec PDFs). */
  href?: string;
  /** Short clause after the link telling a crawler what it will find there. */
  note?: string;
};
export type SanityLlmsGroup = {
  _key?: string;
  heading?: string;
  /** Renders under the trailing "Optional" heading rather than its own. */
  isOptional?: boolean;
  links?: SanityLlmsLink[];
};
export type SanityLlmsSettings = {
  title?: string;
  description?: string;
  groups?: SanityLlmsGroup[];
};
export type SanityRequestQuoteHardwareProduct = {
  _id?: string;
  _type?: "hardware";
  title?: string;
  uri?: string;
  productCaption?: string;
  productImage?: SanityMediaValue;
  /** Add-on `_key` values are stable UI identifiers — preserve them end to end. */
  productAddOns?: SanityProductAddOn[];
};
export type SanityRequestQuoteSoftwareProduct = {
  _id?: string;
  _type?: "software";
  title?: string;
  uri?: string;
  productCaption?: string;
  productImage?: SanityMediaValue;
  productIsFree?: boolean;
};
export type SanityRequestQuoteSettings = {
  _id?: string;
  _type?: "requestQuoteSettings";
  show?: boolean;
  text?: string;
  headline?: string;
  body?: SanityRichTextValue;
  marketoFormId?: string;
  hardwareProducts?: SanityRequestQuoteHardwareProduct[];
  softwareProducts?: SanityRequestQuoteSoftwareProduct[];
  successTitle?: string;
  successBody?: SanityRichTextValue;
  successImage?: SanityMediaValue;
  privacyCheckboxLabel?: string;
  marketingCheckboxLabel?: string;
};

/** Rev7 + Rev8 quote dialogs loaded together for the app layout. */
export type SanityRequestQuoteGlobals = {
  rev7?: SanityRequestQuoteSettings | null;
  rev8?: SanityRequestQuoteSettings | null;
};
export type SanityRoutePopup = {
  show?: boolean;
  title?: string;
  text?: SanityRichTextValue;
};
export type SanityRouteSettings = {
  navTheme?: SanityNavTheme;
  pagePattern?: SanityPattern;
  footerPattern?: SanityPattern;
  pageMode?: SanityPageMode;
  designVersion?: SanityDesignVersion;
  customLogo?: SanityMediaValue;
  isHighlighted?: boolean;
  hideFooter?: boolean;
  backButton?: boolean;
  pageNavAdditionalLinks?: Array<SanityNavigationLink | SanityNavigationAdditionalDropdown>;
  supportLink?: SanityLinkValue;
  supportLinkVariant?: SanitySupportLinkVariant;
  subNavLinks?: SanityNavigationLink[];
  subNavMode?: SanitySubNavMode;
  popup?: SanityRoutePopup;
  /** Opt-in subtle vertical parallax for in-flow images on this route. */
  enableImageParallax?: boolean;
};
export type SanityPageBuilderSectionSettings = {
  sectionTitle?: string;
  sectionId?: {
    current?: string;
  };
  anchorName?: string;
  hasTopDivider?: boolean;
  mode?: "light" | "dark";
  theme?: "white" | "grey";
  /**
   * Extra vertical gap rendered AFTER this section, replacing the practice of
   * inserting a dedicated `spacer` pageBuilder section. `default` = whatever
   * the section naturally renders, `more` = adds a spacer-sized gap below,
   * `none` = collapses to no gap.
   */
  spacingBelow?: "default" | "more" | "none";
};
export type SanityPageBuilderSectionMeta = {
  sectionKind?: string;
  isLibrarySection?: boolean;
  isShareableSection?: boolean;
};
export type SanityPageBuilderMediaItem = {
  _key?: string;
  _type?: "item";
  media?: SanityMediaValue;
};
export type SanityPageBuilderTextOnlySection = {
  _type: "wild.pageBuilder.section.textOnly";
  text?: SanityRichTextValue;
  /** Column width of the text body; defaults to `large`. */
  layout?: "full" | "large" | "medium";
};
export type SanityPageBuilderMediaOnlySection = {
  _type: "wild.pageBuilder.section.mediaOnly";
  media?: SanityMediaValue;
  /** Presentation of the media; defaults to `boxed`. */
  layout?: "boxed" | "full-bleed";
  /** Width of the boxed media column; defaults to `full`. */
  assetLayout?: "full" | "large" | "medium";
  /** Render boxed images at their intrinsic ratio instead of 2/1. */
  useOriginalRatio?: boolean;
  /** Render on the static white surface. */
  isHighlighted?: boolean;
  /** Lift the section above the page pattern so the pattern does not show through. */
  hasSolidBackground?: boolean;
  /** Design system 3.0 only ("Media Banner"): copy laid over the media. */
  tag?: string;
  headline?: string;
  /** Plain multi-line text. */
  text?: string;
  link?: SanityLinkValue;
  /** 3.0: no top-right notch from lg (rev-8 frame 2188:13646). */
  hideDesktopNotch?: boolean;
};
export type SanityPageBuilderMediaSliderSection = {
  _type: "wild.pageBuilder.section.mediaSlider";
  items?: SanityPageBuilderMediaItem[];
};
export type SanityPageBuilderQuoteSection = {
  _type: "wild.pageBuilder.section.quote";
  author?: {
    description?: string;
    name?: string;
  };
  quoteText?: SanityRichTextValue;
};
export type SanityPageBuilderAccordionListItemBulletPoint = {
  _key?: string;
  _type?: "accordionListItemBulletPoint";
  title?: string;
  text?: SanityRichTextValue;
};
export type SanityPageBuilderAccordionListItem = {
  _key?: string;
  _type?: "accordionListItem";
  title?: string;
  shortDescription?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  bulletPoints?: SanityPageBuilderAccordionListItemBulletPoint[];
};
export type SanityPageBuilderAccordionListSection = {
  _type: "ouster.pageBuilder.section.accordionList";
  items?: SanityPageBuilderAccordionListItem[];
};
export type SanityPageBuilderBannerSection = {
  _type: "ouster.pageBuilder.section.banner";
  tag?: string;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  isHighlighted?: boolean;
};
export type SanityPageBuilderBannerNotesBackground = {
  _key?: string;
  _type?: "bannerNotesBackground";
  media?: SanityMediaValue;
};
export type SanityPageBuilderBannerNotesNote = {
  _key?: string;
  _type?: "bannerNotesNote";
  topic?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
};
export type SanityPageBuilderBannerNotesSection = {
  _type: "ouster.pageBuilder.section.bannerNotes";
  headline?: string;
  text?: SanityRichTextValue;
  linkText?: string;
  media?: SanityMediaValue;
  backgrounds?: SanityPageBuilderBannerNotesBackground[];
  notes?: SanityPageBuilderBannerNotesNote[];
};
export type SanityPageBuilderBenefitsBenefit = {
  _key?: string;
  _type?: "benefitsBenefit";
  title?: string;
  text?: string;
};
export type SanityPageBuilderBenefitsSection = {
  _type: "ouster.pageBuilder.section.benefits";
  benefits?: SanityPageBuilderBenefitsBenefit[];
};
export type SanityPageBuilderBenefitListItem = {
  _key?: string;
  _type?: "benefitListItem";
  title?: string;
  text?: SanityRichTextValue;
};
export type SanityPageBuilderBenefitsListSection = {
  _type: "ouster.pageBuilder.section.benefitsList";
  items?: SanityPageBuilderBenefitListItem[];
};
export type SanityPageBuilderCallToActionListItem = {
  _key?: string;
  _type?: "callToActionListItem";
  headline?: string;
  text?: string;
  link?: SanityLinkValue;
  image?: SanityMediaValue;
};
export type SanityPageBuilderCallToActionListSection = {
  _type: "ouster.pageBuilder.section.callToActionList";
  showLinks?: boolean;
  items?: SanityPageBuilderCallToActionListItem[];
};
export type SanityPageBuilderCallToActionCardSection = {
  _type: "ouster.pageBuilder.section.callToActionCard";
  headline?: string;
  text?: string;
  primaryLink?: SanityLinkValue;
  secondaryLink?: SanityLinkValue;
};
export type SanityPageBuilderProductDownloadsTableSection = {
  _type: "ouster.pageBuilder.section.productDownloadsTable";
  productsNewData?: SanityProductsNewData;
};
export type SanityPageBuilderChipEvolutionGraphBulletPoint = {
  _key?: string;
  _type?: "chipEvolutionGraphBulletPoint";
  text?: string;
};
export type SanityPageBuilderChipEvolutionGraphItem = {
  _key?: string;
  _type?: "chipEvolutionGraphItem";
  image?: SanityMediaValue;
  name?: string;
  year?: string;
  range?: number;
  pointsPerSecond?: number;
};
export type SanityPageBuilderChipEvolutionGraphSection = {
  _type: "ouster.pageBuilder.section.chipEvolutionGraph";
  headline?: string;
  text?: SanityRichTextValue;
  bulletPoints?: SanityPageBuilderChipEvolutionGraphBulletPoint[];
  showXAxisData?: boolean;
  items?: SanityPageBuilderChipEvolutionGraphItem[];
  linkText?: string;
  link?: SanityLinkValue;
};
export type SanityPageBuilderContentGallerySpec = {
  _key?: string;
  _type?: "contentGallerySpec";
  label?: string;
  value?: string;
};
export type SanityPageBuilderContentGalleryItem = {
  _key?: string;
  _type?: "contentGalleryItem";
  media?: SanityMediaValue;
  title?: string;
  description?: SanityRichTextValue;
  specs?: SanityPageBuilderContentGallerySpec[];
  link?: SanityLinkValue;
};
export type SanityPageBuilderContentGallerySection = {
  _type: "ouster.pageBuilder.section.contentGallery";
  isHighlighted?: boolean;
  hasCounters?: boolean;
  items?: SanityPageBuilderContentGalleryItem[];
};
export type SanityPageBuilderFeaturedGalleryReferencedDocument = {
  _id?: string;
  _type?: "article" | "caseStudy" | "webinar";
  title?: string;
  headline?: string;
  uri?: string;
  href?: string;
  url?: string;
  hero?: Array<Pick<SanityHero, "_type"> & {
    text?: SanityRichTextValue;
    media?: SanityMediaValue;
  }>;
  postDate?: string;
  date?: string;
  linkText?: string;
  // Reference-card tags. `article` carries a single `articleCategory` here;
  // `industry` is the single `industryCategory` for `article` and the
  // `industries[]` array for `caseStudy` (webinar has neither).
  category?: SanityReferenceSummary;
  industry?: SanityReferenceSummary | SanityReferenceSummary[];
};
export type SanityPageBuilderFeaturedGalleryGeneralCard = {
  _key?: string;
  _type?: "featuredGalleryGeneralCard";
  media?: SanityMediaValue;
  hasCounter?: boolean;
  title?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderFeaturedGalleryArticleCard = {
  _key?: string;
  _type?: "featuredGalleryArticleCard";
  article?: SanityPageBuilderFeaturedGalleryReferencedDocument;
};
export type SanityPageBuilderFeaturedGalleryCaseStudyCard = {
  _key?: string;
  _type?: "featuredGalleryCaseStudyCard";
  caseStudy?: SanityPageBuilderFeaturedGalleryReferencedDocument;
};
export type SanityPageBuilderFeaturedGalleryWebinarCard = {
  _key?: string;
  _type?: "featuredGalleryWebinarCard";
  webinar?: SanityPageBuilderFeaturedGalleryReferencedDocument;
};
export type SanityPageBuilderFeaturedGalleryItem = SanityPageBuilderFeaturedGalleryGeneralCard | SanityPageBuilderFeaturedGalleryArticleCard | SanityPageBuilderFeaturedGalleryCaseStudyCard | SanityPageBuilderFeaturedGalleryWebinarCard;
export type SanityPageBuilderFeaturedGallerySection = {
  _type: "ouster.pageBuilder.section.featuredGallery";
  items?: SanityPageBuilderFeaturedGalleryItem[];
};
export type SanityPageBuilderCoreFeaturesGalleryItem = {
  _key?: string;
  _type?: "coreFeaturesGalleryItem";
  media?: SanityMediaValue;
  link?: SanityLinkValue;
  title?: string;
  description?: SanityRichTextValue;
};
export type SanityPageBuilderCoreFeaturesGallerySection = {
  _type: "ouster.pageBuilder.section.coreFeaturesGallery";
  items?: SanityPageBuilderCoreFeaturesGalleryItem[];
};
export type SanityPageBuilderCoreVideoFeaturesGalleryItem = {
  _key?: string;
  _type?: "coreVideoFeaturesGalleryItem";
  headline?: string;
  description?: SanityRichTextValue;
  media?: SanityMediaValue;
};
export type SanityPageBuilderCoreVideoFeaturesGallerySection = {
  _type: "ouster.pageBuilder.section.coreVideoFeaturesGallery";
  headline?: string;
  isHighlighted?: boolean;
  items?: SanityPageBuilderCoreVideoFeaturesGalleryItem[];
};
export type SanityPageBuilderFaqQuestion = {
  _key?: string;
  _type?: "faqQuestion";
  question?: string;
  /**
   * Authored anchor slug for this question. Optional: the web renderer derives
   * one from the question text when it is absent, so pre-existing content keeps
   * working without a migration.
   */
  questionSlug?: string;
  answer?: SanityRichTextValue;
};
export type SanityPageBuilderFaqSection = {
  _type: "ouster.pageBuilder.section.faq";
  headline?: string;
  link?: SanityLinkValue;
  questions?: SanityPageBuilderFaqQuestion[];
};

/** Longest slug we are willing to emit; long anchors are unreadable and get truncated in citations. */
export const SANITY_FAQ_SLUG_MAX_LENGTH = 60;

/**
 * Turn an FAQ question into an anchor slug.
 *
 * Lives here rather than in `web` or `studio` because both need byte-identical
 * output: Studio's "Generate" button must reproduce exactly what the web
 * renderer derives, otherwise pressing it would silently move every anchor.
 *
 * Callers on the web side must strip stega encoding from `text` first — this
 * function is deliberately dependency-free and cannot do it itself.
 */
export function slugifySanityFaqQuestion(text: string): string {
  const slug = text.normalize("NFKD")
  // Drop the combining marks NFKD just split off, so "Auflösung" -> "auflosung".
  .replace(/[\u0300-\u036f]/g, "").toLowerCase()
  // "R&D" would otherwise collapse to "r-d"; spell it out before the sweep below.
  .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (slug.length <= SANITY_FAQ_SLUG_MAX_LENGTH) {
    return slug;
  }

  // Prefer clamping on a word boundary, but a single word longer than the limit
  // has no boundary to clamp on — cut it hard rather than return an overlong slug.
  const clamped = slug.slice(0, SANITY_FAQ_SLUG_MAX_LENGTH);
  const lastBoundary = clamped.lastIndexOf("-");
  return (lastBoundary > 0 ? clamped.slice(0, lastBoundary) : clamped).replace(/-+$/, "");
}
export type SanityPageBuilderDeepDiveGalleryStep = {
  _key?: string;
  _type?: "deepDiveGalleryStep";
  headline?: string;
  text?: SanityRichTextValue;
  image?: SanityMediaValue;
  video?: SanityMediaValue;
  videoStart?: number;
  videoEnd?: number;
  isIntro?: boolean;
  hasBlur?: boolean;
};
export type SanityPageBuilderDeepDiveGallerySection = {
  _type: "ouster.pageBuilder.section.deepDiveGallery";
  hasOverlay?: boolean;
  video?: SanityMediaValue;
  steps?: SanityPageBuilderDeepDiveGalleryStep[];
};
export type SanityPageBuilderFeaturesComparisonTableItem = {
  _key?: string;
  _type?: "featuresComparisonTableItem";
  feature?: string;
  featuredIn?: string[];
};
export type SanityPageBuilderFeaturesComparisonTableSection = {
  _type: "ouster.pageBuilder.section.featuresComparisonTable";
  items?: SanityPageBuilderFeaturesComparisonTableItem[];
};
export type SanityPageBuilderGallerySlide = {
  _key?: string;
  _type?: "gallerySlide";
  title?: string;
  media?: SanityMediaValue;
  contentPlacement?: "inset" | "bottom";
  text?: SanityRichTextValue;
  label?: string;
  link?: SanityLinkValue;
  supportLink?: SanityLinkValue;
};
export type SanityPageBuilderGallerySection = {
  _type: "ouster.pageBuilder.section.gallery";
  headline?: string;
  eyebrow?: string;
  text?: SanityRichTextValue;
  layout?: "boxed" | "full-bleed";
  hasInsetDividers?: boolean;
  isHighlighted?: boolean;
  slides?: SanityPageBuilderGallerySlide[];
};
export type SanityPageBuilderGraphicStatsStat = {
  _key?: string;
  _type?: "graphicStatsStat";
  stat?: string;
};
export type SanityPageBuilderStatsGraphicBar = {
  _key?: string;
  _type?: "statsGraphicBar";
  name?: string;
  value?: number;
  highlighted?: boolean;
  tag?: string;
};
export type SanityPageBuilderGraphicStatsSection = {
  _type: "ouster.pageBuilder.section.graphicStats";
  headline?: string;
  caption?: string;
  callToActionText?: string;
  link?: SanityLinkValue;
  max?: number;
  graphic?: SanityPageBuilderStatsGraphicBar[];
  stats?: SanityPageBuilderGraphicStatsStat[];
};
export type SanityPageBuilderGraphicCardsCard = {
  _key?: string;
  _type?: "graphicCardsCard";
  image?: SanityMediaValue;
  title?: string;
  text?: SanityRichTextValue;
};
export type SanityPageBuilderGraphicCardsSection = {
  _type: "ouster.pageBuilder.section.graphicCards";
  cards?: SanityPageBuilderGraphicCardsCard[];
};
export type SanityPageBuilderGeneralGridCard = {
  _key?: string;
  _type?: "generalGridCard";
  media?: SanityMediaValue;
  title?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderGridCardsSection = {
  _type: "ouster.pageBuilder.section.gridCards";
  cards?: SanityPageBuilderGeneralGridCard[];
};
export type SanityPageBuilderMediaCardsCardLink = {
  _key?: string;
  _type?: "mediaCardsCardLink";
  link?: SanityLinkValue;
};
export type SanityPageBuilderMediaCardsCard = {
  _key?: string;
  _type?: "mediaCardsCard";
  media?: SanityMediaValue;
  headline?: string;
  text?: SanityRichTextValue;
  links?: SanityPageBuilderMediaCardsCardLink[];
};
export type SanityPageBuilderMediaCardsSection = {
  _type: "ouster.pageBuilder.section.mediaCards";
  cards?: SanityPageBuilderMediaCardsCard[];
};
export type SanityPageBuilderPartnerGalleryItem = {
  _key?: string;
  _type?: "partnerGalleryItem";
  logo?: SanityMediaValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
  title?: string;
  description?: SanityRichTextValue;
};
export type SanityPageBuilderPartnerGallerySection = {
  _type: "ouster.pageBuilder.section.partnerGallery";
  /** 3.0 only (hidden in Studio unless the route's `designVersion` is "3"). */
  eyebrow?: string;
  /** 3.0 only. */
  headline?: string;
  /** 3.0 only: full-bleed visual behind the header and the partner card. */
  backgroundMedia?: SanityMediaValue;
  isHighlighted?: boolean;
  showGalleryNavigation?: boolean;
  items?: SanityPageBuilderPartnerGalleryItem[];
};
export type SanityPageBuilderStackedGallerySpec = {
  _key?: string;
  _type?: "stackedGallerySpec";
  label?: string;
  value?: string;
};
export type SanityPageBuilderStackedGalleryItem = {
  _key?: string;
  _type?: "stackedGalleryItem";
  media?: SanityMediaValue;
  category?: string;
  cta?: SanityLinkValue;
  title?: string;
  description?: SanityRichTextValue;
  link?: SanityLinkValue;
  specs?: SanityPageBuilderStackedGallerySpec[];
};
export type SanityPageBuilderStackedGallerySection = {
  _type: "ouster.pageBuilder.section.stackedGallery";
  items?: SanityPageBuilderStackedGalleryItem[];
};
export type SanityPageBuilderMediaCarouselSlide = {
  _key?: string;
  _type?: "mediaCarouselSlide";
  media?: SanityMediaValue;
};
export type SanityPageBuilderMediaCarouselSection = {
  _type: "ouster.pageBuilder.section.mediaCarousel";
  slides?: SanityPageBuilderMediaCarouselSlide[];
};
export type SanityPageBuilderMediaHighlightsItem = {
  _key?: string;
  _type?: "mediaHighlightsItem";
  left?: string;
  right?: string;
  /** 3.0 Specs only: the text after the chip (`left`) and label (`right`). */
  value?: string;
};
export type SanityPageBuilderMediaHighlightsSection = {
  _type: "ouster.pageBuilder.section.mediaHighlights";
  /** 3.0 only: "UI / Media Highlight" content. Missing means "headline". */
  variant?: "headline" | "specs";
  isHighlighted?: boolean;
  hasSolidBackground?: boolean;
  disableImageParallax?: boolean;
  media?: SanityMediaValue;
  headline?: string;
  eyebrow?: string;
  title?: string;
  link?: SanityLinkValue;
  highlights?: SanityPageBuilderMediaHighlightsItem[];
};
export type SanityPageBuilderMediaWithHighlightsItem = {
  _key?: string;
  _type?: "mediaWithHighlightsItem";
  label?: string;
  text?: string;
};
export type SanityPageBuilderMediaWithHighlightsSection = {
  _type: "ouster.pageBuilder.section.mediaWithHighlights";
  media?: SanityMediaValue;
  hasCounters?: boolean;
  highlights?: SanityPageBuilderMediaWithHighlightsItem[];
};
export type SanityPageBuilderNewsletterSection = {
  _type: "ouster.pageBuilder.section.newsletter";
  headline?: string;
};
export type SanityPageBuilderOfficesMapSection = {
  _type: "ouster.pageBuilder.section.officesMap";
};
export type SanityPageBuilderOpenJobPositionsSection = {
  _type: "ouster.pageBuilder.section.openJobPositions";
  successStepTitle?: string;
  successStepDescription?: string;
  successStepMedia?: SanityMediaValue;
};
export type SanityPageBuilderPartnersSection = {
  _type: "ouster.pageBuilder.section.partners";
};
export type SanityPageBuilderPointCloudTopMedia = {
  media?: SanityMediaValue;
};
export type SanityPageBuilderPointCloudBottomMedia = {
  media?: SanityMediaValue;
  scale?: string;
};
export type SanityPageBuilderPointCloudSection = {
  _type: "ouster.pageBuilder.section.pointCloud";
  headline?: string;
  minZoom?: string;
  maxZoom?: string;
  media?: SanityMediaValue;
  topMedia?: SanityPageBuilderPointCloudTopMedia;
  bottomMedia?: SanityPageBuilderPointCloudBottomMedia;
};
export type SanityHardwareSpecValue = {
  _key?: string;
  _type?: "hardwareSpecValue";
  specKey?: string;
  value?: string;
};
export type SanityHardwareCardDocument = SanityHardwareMetadata & {
  _id?: string;
  _type: "hardware";
  title?: string;
  uri?: string;
  specValues?: SanityHardwareSpecValue[];
};
export type SanityPageBuilderProductRowSection = {
  _type: "ouster.pageBuilder.section.productRow";
  products?: SanityHardwareCardDocument[];
};
export type SanityMerchCardDocument = {
  _id?: string;
  _type: "merchProduct";
  title?: string;
  slug?: string;
  price?: number;
  soldOut?: boolean;
  sizeOptions?: string[];
  image?: SanityMediaValue;
};
export type SanityPageBuilderShopGridSection = {
  _type: "ouster.pageBuilder.section.shopGrid";
  products?: SanityMerchCardDocument[];
};
export type SanityMerchSpecRow = {
  _key?: string;
  label?: string;
  value?: string;
};
export type SanityMerchProductDocument = {
  _id?: string;
  _type: "merchProduct";
  title?: string;
  slug?: string;
  price?: number;
  description?: SanityRichTextValue;
  image?: SanityMediaValue;
  sizeOptions?: string[];
  specs?: SanityMerchSpecRow[];
  details?: SanityRichTextValue;
  materials?: SanityRichTextValue;
  limitNote?: string;
  soldOut?: boolean;
};
export type SanityPageBuilderShopProductSection = {
  _type: "ouster.pageBuilder.section.shopProduct";
  product?: SanityMerchProductDocument;
};
export type SanityPageBuilderShopHeroSection = {
  _type: "ouster.pageBuilder.section.shopHero";
  headline?: string;
  text?: SanityRichTextValue;
};
export type SanityPageBuilderShopStatementSection = {
  _type: "ouster.pageBuilder.section.shopStatement";
  headline?: string;
};
export type SanityPageBuilderProductSpecsItem = {
  _key?: string;
  _type?: "productSpecsItem";
  media?: SanityMediaValue;
  spec?: string;
  title?: string;
  description?: SanityRichTextValue;
};
export type SanityPageBuilderProductSpecsSection = {
  _type: "ouster.pageBuilder.section.productSpecs";
  items?: SanityPageBuilderProductSpecsItem[];
};
export type SanityPageBuilderRgbItem = {
  _key?: string;
  _type?: "rgbItem";
  media?: SanityMediaValue;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderRgbSection = {
  _type: "ouster.pageBuilder.section.rgb";
  headline?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
  slides?: SanityPageBuilderRgbItem[];
};
export type SanityPageBuilderScrollFeaturesFeature = {
  _key?: string;
  _type?: "scrollFeaturesFeature";
  headline?: string;
  media?: SanityMediaValue;
};
export type SanityPageBuilderScrollFeaturesSection = {
  _type: "ouster.pageBuilder.section.scrollFeatures";
  features?: SanityPageBuilderScrollFeaturesFeature[];
};
export type SanityPageBuilderScrollHeadlinesHeadline = {
  _key?: string;
  _type?: "scrollHeadlinesHeadline";
  topDistance?: number;
  bottomDistance?: number;
  headline?: string;
};
export type SanityPageBuilderScrollHeadlinesSection = {
  _type: "ouster.pageBuilder.section.scrollHeadlines";
  topDistance?: number;
  startVisible?: boolean;
  video?: SanityMediaValue;
  label?: string;
  hasOverlay?: boolean;
  headlines?: SanityPageBuilderScrollHeadlinesHeadline[];
};
export type SanityPageBuilderScrollSpecsSpec = {
  _key?: string;
  _type?: "scrollSpecsSpec";
  title?: string;
  text?: string;
};
export type SanityPageBuilderScrollSpecsSection = {
  _type: "ouster.pageBuilder.section.scrollSpecs";
  headline?: string;
  media?: SanityMediaValue;
  specs?: SanityPageBuilderScrollSpecsSpec[];
};
export type SanityPageBuilderSensorFlowGraphicsGraphic = {
  _key?: string;
  _type?: "sensorFlowGraphicsGraphic";
  headline?: string;
  leadingText?: string;
  union?: string;
  trailingText?: string;
  hasHorizontalLayout?: boolean;
};
export type SanityPageBuilderSensorFlowGraphicsSection = {
  _type: "ouster.pageBuilder.section.sensorFlowGraphics";
  headline?: string;
  media?: SanityMediaValue;
  graphics?: SanityPageBuilderSensorFlowGraphicsGraphic[];
};
export type SanitySpecsListLayout = "full" | "large" | "medium";
export type SanitySpecsListColumns = "2Columns" | "3Columns" | "4Columns";
export type SanityPageBuilderSpecsListSpec = {
  _key?: string;
  _type?: "specsListSpec";
  title?: string;
  subTitle?: string;
  /** Design system 3.0 only: the image shown over the row on hover. */
  image?: SanityMediaValue;
};
export type SanityPageBuilderSpecsListSection = {
  _type: "ouster.pageBuilder.section.specsList";
  specs?: SanityPageBuilderSpecsListSpec[];
  link?: SanityLinkValue;
  isHighlighted?: boolean;
  layout?: SanitySpecsListLayout;
  columns?: SanitySpecsListColumns;
  hasBorder?: boolean;
};
export type SanityPageBuilderSpecsRowSpec = {
  _key?: string;
  _type?: "specsRowSpec";
  name?: string;
  value?: string;
};
export type SanityPageBuilderSpecsRowSection = {
  _type: "ouster.pageBuilder.section.specsRow";
  specs?: SanityPageBuilderSpecsRowSpec[];
};
export type SanityTechnicalComparisonLinkIcon = "default" | "download" | "arrow-down" | "arrow-up";
export type SanityPageBuilderTechnicalComparisonSpec = {
  _key?: string;
  _type?: "technicalComparisonSpec";
  label?: string;
  value?: string;
};
export type SanityPageBuilderTechnicalComparisonLink = {
  _key?: string;
  _type?: "technicalComparisonLink";
  link?: SanityLinkValue;
  linkIcon?: SanityTechnicalComparisonLinkIcon;
};

/** A titled block of the 3.0 specs drawer; an untitled group shows its rows without a heading. */
export type SanityPageBuilderTechnicalComparisonSpecGroup = {
  _key?: string;
  _type?: "technicalComparisonSpecGroup";
  title?: string;
  specs?: SanityPageBuilderTechnicalComparisonSpec[];
};
export type SanityPageBuilderTechnicalComparisonItem = {
  _key?: string;
  _type?: "technicalComparisonItem";
  productImage?: SanityMediaValue;
  illustration?: SanityMediaValue;
  /** 3.0 only: outline drawing for unselected product tabs and the specs drawer. */
  productLineArt?: SanityMediaValue;
  /** 3.0 only: the larger drawing at the top of the specs drawer. */
  drawerImage?: SanityMediaValue;
  title?: string;
  specs?: SanityPageBuilderTechnicalComparisonSpec[];
  link?: SanityLinkValue;
  supportLink?: SanityLinkValue;
  links?: SanityPageBuilderTechnicalComparisonLink[];
  /** 3.0 only: the full, grouped spec table of the product specs drawer. */
  specGroups?: SanityPageBuilderTechnicalComparisonSpecGroup[];
  /** 3.0 only: the drawer's "Specification sheet" download. */
  specSheet?: SanityLinkValue;
  /** 3.0 only: the product the comparison opens on. */
  isSelected?: boolean;
  /** Byte size of `specSheet` when it links an uploaded file. */
  specSheetFileSize?: number | null;
};
export type SanityPageBuilderTechnicalComparisonSection = {
  _type: "ouster.pageBuilder.section.technicalComparison";
  isHighlighted?: boolean;
  items?: SanityPageBuilderTechnicalComparisonItem[];
};
export type SanityPageBuilderTestimonialsCarouselSlide = {
  _key?: string;
  _type?: "testimonialsCarouselSlide";
  quote?: string;
  media?: SanityMediaValue;
  author?: string;
};
export type SanityPageBuilderTestimonialCarouselSection = {
  _type: "ouster.pageBuilder.section.testimonialCarousel";
  slides?: SanityPageBuilderTestimonialsCarouselSlide[];
  slideshowInterval?: number;
  hasSolidBackground?: boolean;
};
export type SanityPageBuilderTextFactsMediaFact = {
  _key?: string;
  _type?: "textFactsMediaFact";
  title?: string;
  label?: string;
};
export type SanityPageBuilderTextFactsMediaSection = {
  _type: "ouster.pageBuilder.section.textFactsMedia";
  headline?: string;
  eyebrow?: string;
  media?: SanityMediaValue;
  facts?: SanityPageBuilderTextFactsMediaFact[];
};
export type SanityPageBuilderTextMedia12Section = {
  _type: "ouster.pageBuilder.section.textMedia1_2";
  headline?: string;
  hasSpacer?: boolean;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
  /** Design system 3.0 only (hidden in Studio on 2.x pages). */
  eyebrow?: string;
  /** Design system 3.0 only: the section surface. */
  background?: "white" | "sand" | "muted";
  /** Design system 3.0 only: the small second visual, drawn with the media on the left. */
  secondaryMedia?: SanityMediaValue;
};
export type SanityPageBuilderTextMedia13Section = {
  _type: "ouster.pageBuilder.section.textMedia1_3";
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
};
export type SanityPageBuilderTextMedia13BSection = {
  _type: "ouster.pageBuilder.section.textMedia1_3_B";
  headline?: string;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
};
export type SanityPageBuilderTextMediaCItem = {
  _key?: string;
  _type?: "textMediaCItem";
  media?: SanityMediaValue;
  title?: string;
  description?: SanityRichTextValue;
};
export type SanityPageBuilderTextMediaCSection = {
  _type: "ouster.pageBuilder.section.textMediaC";
  headline?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
  items?: SanityPageBuilderTextMediaCItem[];
};
export type SanityPageBuilderToolCardsCard = {
  _key?: string;
  _type?: "toolCardsCard";
  media?: SanityMediaValue;
  title?: string;
  description?: SanityRichTextValue;
  url?: string;
};
export type SanityPageBuilderToolCardsSection = {
  _type: "ouster.pageBuilder.section.toolCards";
  headline?: string;
  isHighlighted?: boolean;
  cards?: SanityPageBuilderToolCardsCard[];
};
export type SanityPageBuilderTwoColumnMediaColumn = {
  _key?: string;
  _type?: "twoColumnMediaColumn";
  media?: SanityMediaValue;
  /** Design system 3.0 only: the card title over the media. */
  title?: string;
  /** Design system 3.0 only: short plain text under the title. */
  description?: string;
  /** Design system 3.0 only: makes the whole card a link. */
  link?: SanityLinkValue;
};
export type SanityPageBuilderTwoColumnMediaSection = {
  _type: "ouster.pageBuilder.section.twoColumnMedia";
  medias?: SanityPageBuilderTwoColumnMediaColumn[];
  /** Design system 3.0 only: the section surface. */
  background?: "white" | "muted";
};
export type SanityPageBuilderHeadlineSection = {
  _type: "ouster.pageBuilder.section.headline";
  headline?: string;
  eyebrow?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  linkVariant?: SanitySupportLinkVariant;
  isHighlighted?: boolean;
  highlight?: boolean;
  hasSolidBackground?: boolean;
  /** 3.0 only: copy on grid line 6 (`half`, missing) or line 7 (`inset`, rev-8 2188:13735). */
  variant?: "half" | "inset";
};
export type SanityPageBuilderHeadlineBSection = {
  _type: "ouster.pageBuilder.section.headline_B";
  headline?: string;
  eyebrow?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  linkVariant?: SanitySupportLinkVariant;
  isHighlighted?: boolean;
  hasSpacer?: boolean;
  hasSolidBackground?: boolean;
  /** 3.0 only: "b" (default) draws "UI / Headline B" (left), "c" "UI / Headline C" (centred). */
  variant?: "b" | "bInset" | "c" | null;
};
export type SanityPageBuilderHeadlineDSection = {
  _type: "ouster.pageBuilder.section.headline_D";
  headline?: string;
  eyebrow?: string;
  link?: SanityLinkValue;
  linkVariant?: SanitySupportLinkVariant;
  isHighlighted?: boolean;
  hasSpacer?: boolean;
  hasSolidBackground?: boolean;
};
export type SanityPageBuilderIntroHeadlineSection = {
  _type: "ouster.pageBuilder.section.introHeadline";
  headline?: string;
  hasSpacer?: boolean;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
  hasSolidBackground?: boolean;
  /** 3.0 only: product logo above the headline (rev-8 2188:13645). */
  logo?: SanityMediaValue;
};
export type SanityPageBuilderIntroHeadlineBSection = {
  _type: "ouster.pageBuilder.section.introHeadline_B";
  eyebrow?: string;
  headline?: string;
  headlineAccent?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
  mediaPosition?: "left" | "right";
  primaryLink?: SanityLinkValue;
  secondaryLink?: SanityLinkValue;
};
export type SanityPageBuilderHighlightHeadlineSection = {
  _type: "ouster.pageBuilder.section.highlightHeadline";
  image?: SanityMediaValue;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  isHighlighted?: boolean;
  hasScrollHighlighting?: boolean;
  isBig?: boolean;
  hasSolidBackground?: boolean;
  /** 3.0 only: "big" (default, sand with corner squares) or "normal" (smaller, on white). */
  variant?: "big" | "bigTitle30" | "normal" | null;
};
export type SanityPageBuilderHighlightsHighlight = {
  _key?: string;
  _type?: "highlightsHighlight";
  title?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderHighlightsSection = {
  _type: "ouster.pageBuilder.section.highlights";
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  hasCounters?: boolean;
  highlights?: SanityPageBuilderHighlightsHighlight[];
  hasFooter?: boolean;
  footerTitle?: string;
  footerLogo?: SanityMediaValue;
};
export type SanityPageBuilderHighlightHeadlinesItem = {
  _key?: string;
  _type?: "highlightHeadlinesHeadline";
  image?: SanityMediaValue;
  headline?: string;
  text?: SanityRichTextValue;
};
export type SanityPageBuilderHighlightHeadlinesSection = {
  _type: "ouster.pageBuilder.section.highlightHeadlines";
  items?: SanityPageBuilderHighlightHeadlinesItem[];
};
export type SanityPageBuilderLinkListItem = {
  _key?: string;
  _type?: "linkListLink";
  title?: string;
  link?: SanityLinkValue;
};
export type SanityPageBuilderLinkListSection = {
  _type: "ouster.pageBuilder.section.linkList";
  headline?: string;
  items?: SanityPageBuilderLinkListItem[];
};
export type SanityPageBuilderLinkBarsLink = {
  _key?: string;
  _type?: "linkBarsItemLink";
  link?: SanityLinkValue;
  caption?: string;
};
export type SanityPageBuilderLinkBarsItem = {
  _key?: string;
  _type?: "linkBarsItem";
  title?: string;
  links?: SanityPageBuilderLinkBarsLink[];
};
export type SanityPageBuilderLinkBarsSection = {
  _type: "ouster.pageBuilder.section.linkBars";
  items?: SanityPageBuilderLinkBarsItem[];
};
export type SanityPageBuilderInfoCardsCard = {
  _key?: string;
  _type?: "infoCardsCard";
  media?: SanityMediaValue;
  title?: string;
  description?: SanityRichTextValue;
  url?: string;
};
export type SanityPageBuilderInfoCardsSection = {
  _type: "ouster.pageBuilder.section.infoCards";
  headline?: string;
  isHighlighted?: boolean;
  cards?: SanityPageBuilderInfoCardsCard[];
};

// --- Editorial Cards ---
export type SanityPageBuilderEditorialCardsReferencedDocument = SanityPageBuilderFeaturedGalleryReferencedDocument;
export type SanityPageBuilderEditorialCardsInlineCard = {
  _key?: string;
  _type?: "editorialCardsGeneralCard" | "editorialCardsShowcaseCard";
  media?: SanityMediaValue;
  hasCounter?: boolean;
  title?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderEditorialCardsArticleCard = {
  _key?: string;
  _type?: "editorialCardsArticleCard";
  article?: SanityPageBuilderEditorialCardsReferencedDocument;
};

/**
 * Compatibility shape for a pre-page-builder editorial card that was stored
 * as a root section. New Studio content must use the Editorial Cards parent.
 */
export type SanityPageBuilderLegacyEditorialCardsArticleCardSection = Omit<SanityPageBuilderEditorialCardsArticleCard, "_type"> & {
  _type: "editorialCardsArticleCard";
};
export type SanityPageBuilderEditorialCardsCaseStudyCard = {
  _key?: string;
  _type?: "editorialCardsCaseStudyCard";
  caseStudy?: SanityPageBuilderEditorialCardsReferencedDocument;
};
export type SanityPageBuilderEditorialCardsWebinarCard = {
  _key?: string;
  _type?: "editorialCardsWebinarCard";
  webinar?: SanityPageBuilderEditorialCardsReferencedDocument;
};
export type SanityPageBuilderEditorialCardsCard = SanityPageBuilderEditorialCardsInlineCard | SanityPageBuilderEditorialCardsArticleCard | SanityPageBuilderEditorialCardsCaseStudyCard | SanityPageBuilderEditorialCardsWebinarCard;
export type SanityPageBuilderEditorialCardsSection = {
  _type: "ouster.pageBuilder.section.editorialCards";
  eyebrow?: string;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  hasFilter?: boolean;
  /** Render on the primary surface instead of the secondary one. */
  isHighlighted?: boolean;
  cards?: SanityPageBuilderEditorialCardsCard[];
};

// --- Reference collections ---
export type SanityPageBuilderDistributorsSection = {
  _type: "ouster.pageBuilder.section.distributors";
  industries?: SanityReferenceSummary[];
};
export type SanityPageBuilderExternalProductsCard = {
  _key?: string;
  _type?: "externalProductsCard";
  title?: string;
  description?: SanityRichTextValue;
  media?: SanityMediaValue;
  url?: string;
};
export type SanityPageBuilderExternalProductsSection = {
  _type: "ouster.pageBuilder.section.externalProducts";
  eyebrow?: string;
  headline?: string;
  text?: SanityRichTextValue;
  cards?: SanityPageBuilderExternalProductsCard[];
};
export type SanityPageBuilderHighlightedProductsSection = {
  _type: "ouster.pageBuilder.section.highlightedProducts";
  headline?: string;
  text?: string;
  products?: SanityHardwareCardDocument[];
  link?: SanityLinkValue;
  isHighlighted?: boolean;
  hasFilter?: boolean;
};

// --- Hardware comparison tables ---
export type SanityHardwareComparisonSpec = {
  _key?: string;
  _type?: "hardwareComparisonSpec";
  label?: string;
  icon?: string;
  customIcon?: SanityMediaValue;
};
export type SanityPageBuilderHardwareComparisonTableSection = {
  _type: "ouster.pageBuilder.section.hardwareComparisonTable";
  hasHighlightDifferencesToggle?: boolean;
  specs?: SanityHardwareComparisonSpec[];
  products?: SanityHardwareCardDocument[];
};
export type SanityPageBuilderHardwareComparisonTableV2Section = {
  _type: "ouster.pageBuilder.section.hardwareComparisonTableV2";
  hasHighlightDifferencesToggle?: boolean;
  highlightInstructions?: string;
  productPreviewImage?: SanityMediaValue;
  specs?: SanityHardwareComparisonSpec[];
  products?: SanityHardwareCardDocument[];
};

// --- Embeds ---
export type SanityPageBuilderIframeSection = {
  _type: "ouster.pageBuilder.section.iframe";
  url?: string;
  title?: string;
  mobileDisclaimerText?: string;
  usabilityDisclaimerText?: string;
  layout?: "full" | "large" | "medium";
};
export type SanityPageBuilderIntroDetailsItem = {
  _key?: string;
  _type?: "introDetailsItem";
  label?: string;
  text?: string;
  baseLink?: string;
};
export type SanityPageBuilderIntroDetailsSection = {
  _type: "ouster.pageBuilder.section.introDetails";
  headline?: string;
  text?: SanityRichTextValue;
  details?: SanityPageBuilderIntroDetailsItem[];
};
export type SanityPageBuilderMarketoEmbedFormSection = {
  _type: "ouster.pageBuilder.section.marketoEmbedForm";
  headline?: string;
  description?: string;
  marketoFormId?: string;
  marketoFormUrl?: string;
  marketoFormInstanceId?: string;
  customCss?: SanityRichTextValue;
  successMessageTitle?: string;
  successMessageDescription?: SanityRichTextValue;
  successMessageImage?: SanityMediaValue;
  hasRedirect?: boolean;
  redirectUrl?: string;
  fullWidth?: boolean;
};
export type SanityPageBuilderMarketoFormSection = {
  _type: "ouster.pageBuilder.section.marketoForm";
  marketoFormId?: string;
  layout?: "default" | "condensed";
  successMessage?: string;
  successMessageText?: SanityRichTextValue;
  successMessageImage?: SanityMediaValue;
  disclaimer?: SanityRichTextValue;
  privacyCheckboxLabel?: string;
  showWatchWebinarButton?: boolean;
};
export type SanityPageBuilderCustomerIoFormField = {
  _key?: string;
  _type?: "customerIoFormField";
  key?: string;
  label?: string;
  type?: "text" | "email" | "tel" | "textarea" | "select";
  placeholder?: string;
  required?: boolean;
  options?: string[];
  /** Where the submitted value lands in Customer.io. Default: "both". */
  destination?: "both" | "attribute" | "eventProperty";
};

/** Dereferenced `customerioEvent` document (event vocabulary entry). */
export type SanityCustomerIoEventRef = {
  _id: string;
  title?: string;
  eventName?: string;
  description?: string;
};

/** Author-set static metadata pair merged into the tracked event's data. */
export type SanityCustomerIoEventMetadataPair = {
  _key?: string;
  key?: string;
  value?: string;
};
export type SanityPageBuilderCustomerIoFormSection = {
  _type: "ouster.pageBuilder.section.customerIoForm";
  headline?: string;
  description?: string;
  formName?: string;
  fields?: SanityPageBuilderCustomerIoFormField[];
  layout?: "default" | "condensed";
  submitLabel?: string;
  successMessage?: string;
  successMessageText?: SanityRichTextValue;
  successMessageImage?: SanityMediaValue;
  disclaimer?: SanityRichTextValue;
  privacyCheckboxLabel?: string;
  /** Referenced event vocabulary doc; null/absent means the default `form_submit`. */
  event?: SanityCustomerIoEventRef | null;
  eventMetadata?: SanityCustomerIoEventMetadataPair[];
};

// --- Media grid family ---
/** Design system 3.0 only: an image layered over a media grid card. Offsets are % of the card. */
export type SanityPageBuilderMediaGridDecoration = {
  _key?: string;
  _type?: "mediaDecoration";
  image?: SanityMediaValue;
  left?: number;
  top?: number;
  width?: number;
  /** Travel each way while the card crosses the screen, in % of the card height. */
  parallax?: number;
  glass?: boolean;
  /** Glass blur in px (default 24). */
  glassBlur?: number;
  mobileLeft?: number;
  mobileTop?: number;
  mobileWidth?: number;
};
export type SanityPageBuilderMediaGridContentImageCard = {
  _key?: string;
  _type?: "contentImageCard";
  media?: SanityMediaValue;
  /** Design system 3.0 only: black layer over the media, in % opacity. */
  mediaShade?: number;
  /** Design system 3.0 only: the dark layer fades from half `mediaShade` at the top. */
  mediaShadeGradient?: boolean;
  /** Design system 3.0 only. */
  decorations?: SanityPageBuilderMediaGridDecoration[];
  /** Design system 3.0 only: caption above the title. */
  eyebrow?: string;
  /** Design system 3.0 only: logo shown in a tile beside the title. */
  logo?: SanityMediaValue;
  title?: string;
  description?: SanityRichTextValue;
  link?: SanityLinkValue;
  supportLink?: SanityLinkValue;
};
export type SanityPageBuilderMediaGridContentRow = {
  _key?: string;
  _type?: "contentRow";
  contentRowLayout?: "auto" | "featured-left" | "featured-right";
  cards?: SanityPageBuilderMediaGridContentImageCard[];
};
export type SanityPageBuilderMediaGridMedia = {
  _key?: string;
  _type?: "mediaGridMedia";
  media?: SanityMediaValue;
};
export type SanityPageBuilderOusterMediaGridSection = {
  _type: "ouster.pageBuilder.section.mediaGrid";
  isHighlighted?: boolean;
  hasSolidBackground?: boolean;
  /** Design system 3.0 only: a grid of rows (default) or one carousel of every card. */
  cardsLayout?: "grid" | "carousel";
  rows?: SanityPageBuilderMediaGridContentRow[];
};
export type SanityPageBuilderHighlightedCardsItem = {
  _key?: string;
  _type?: "highlightedCardsItem";
  title?: string;
  description?: SanityRichTextValue;
  link?: SanityLinkValue;
  logo?: SanityMediaValue;
  /** 3.0 only (hidden in Studio unless the route's `designVersion` is "3"): fills the card. */
  image?: SanityMediaValue;
};
export type SanityPageBuilderHighlightedCardsSection = {
  _type: "ouster.pageBuilder.section.highlightedCards";
  eyebrow?: string;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  isHighlighted?: boolean;
  cards?: SanityPageBuilderHighlightedCardsItem[];
};
export type SanityPageBuilderLogoSliderLogo = {
  _key?: string;
  _type?: "logoSliderLogo";
  image?: SanityMediaValue;
  url?: string;
};
export type SanityPageBuilderLogoSliderSection = {
  _type: "ouster.pageBuilder.section.logoSlider";
  logos?: SanityPageBuilderLogoSliderLogo[];
  /** 3.0 only: the opposing second row below lg (unset counts as shown). */
  showMobileSecondRow?: boolean;
  /** 3.0 only: the opposing second row from lg too (rev-8 2188:13694). */
  showDesktopSecondRow?: boolean;
};
export type SanityPageBuilderLogoWallSection = {
  _type: "ouster.pageBuilder.section.logoWall";
  isHighlighted?: boolean;
  headline?: string;
  gallery?: SanityMediaValue[];
};
export type SanityPageBuilderSpacerSection = {
  _type: "ouster.pageBuilder.section.spacer";
  isHighlighted?: boolean;
  hasSolidBackground?: boolean;
  isBig?: boolean;
};
export type SanityPageBuilderSocialShareSection = {
  _type: "ouster.pageBuilder.section.socialShare";
  text?: string;
};

// --- Data from the Real World (design system 3.0 only) ---
export type SanityPageBuilderDataFromRealWorldItem = {
  _key?: string;
  _type?: "dataFromRealWorldItem";
  title?: string;
  /** Small square image in the dataset list; falls back to `media`. */
  thumbnail?: SanityMediaValue;
  /** Large panel shown while the item is active (video or image). */
  media?: SanityMediaValue;
  /** Wide image strip (camera frames) along the bottom of the large panel. */
  strip?: SanityMediaValue;
  /** Short mono labels laid over the large panel. */
  tags?: string[];
  /** "View in Ouster Studio" target. */
  link?: SanityLinkValue;
};
export type SanityPageBuilderDataFromRealWorldSection = {
  _type: "ouster.pageBuilder.section.dataFromRealWorld";
  eyebrow?: string;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
  items?: SanityPageBuilderDataFromRealWorldItem[];
};

// --- Rive animation (design system 3.0) ---
/** An uploaded `.riv` file and how to drive it. Empty names use the file's defaults. */
export type SanityRiveValue = {
  /** CDN URL of the `.riv` file. */
  src?: string;
  artboard?: string;
  stateMachine?: string;
  /** Boolean state machine input set while the element is hovered (or in view on touch screens). */
  hoverInput?: string;
};
export const sanityRiveProjection = `{
  "src": file.asset->url,
  artboard,
  stateMachine,
  hoverInput
}`;

// --- Stats with Icons (design system 3.0 only) ---
export type SanityPageBuilderStatsIconsItem = {
  _key?: string;
  _type?: "statsIconsItem";
  label?: string;
  /** Line icon drawn in a 96px square (an SVG upload keeps it sharp). */
  icon?: SanityMediaValue;
  /** Animated icon; the static `icon` shows until it loads and with reduced motion. */
  rive?: SanityRiveValue;
};
export type SanityPageBuilderStatsIconsSection = {
  _type: "ouster.pageBuilder.section.statsIcons";
  items?: SanityPageBuilderStatsIconsItem[];
};

// --- Single Media Swipe (design system 3.0 only) ---
export type SanityPageBuilderMediaSwipeSection = {
  _type: "ouster.pageBuilder.section.mediaSwipe";
  /** Big title cut out of the white overlay; the media shows through its letters. */
  overlayHeadline?: string;
  /** Full-bleed image or video revealed when the overlay swipes away. */
  media?: SanityMediaValue;
  eyebrow?: string;
  headline?: string;
  text?: SanityRichTextValue;
  link?: SanityLinkValue;
};
export type SanityPageBuilderSectionContent = SanityPageBuilderTextOnlySection | SanityPageBuilderMediaOnlySection | SanityPageBuilderMediaSliderSection | SanityPageBuilderQuoteSection | SanityPageBuilderAccordionListSection | SanityPageBuilderBannerSection | SanityPageBuilderBannerNotesSection | SanityPageBuilderBenefitsSection | SanityPageBuilderBenefitsListSection | SanityPageBuilderCallToActionListSection | SanityPageBuilderCallToActionCardSection | SanityPageBuilderProductDownloadsTableSection | SanityPageBuilderCubeExperienceSection | SanityPageBuilderChipEvolutionGraphSection | SanityPageBuilderContentGallerySection | SanityPageBuilderFeaturedGallerySection | SanityPageBuilderCoreFeaturesGallerySection | SanityPageBuilderCoreVideoFeaturesGallerySection | SanityPageBuilderFaqSection | SanityPageBuilderDeepDiveGallerySection | SanityPageBuilderFeaturesComparisonTableSection | SanityPageBuilderGallerySection | SanityPageBuilderGraphicStatsSection | SanityPageBuilderGraphicCardsSection | SanityPageBuilderGridCardsSection | SanityPageBuilderMediaCardsSection | SanityPageBuilderPartnerGallerySection | SanityPageBuilderStackedGallerySection | SanityPageBuilderMediaCarouselSection | SanityPageBuilderMediaHighlightsSection | SanityPageBuilderMediaWithHighlightsSection | SanityPageBuilderNewsletterSection | SanityPageBuilderOfficesMapSection | SanityPageBuilderOpenJobPositionsSection | SanityPageBuilderPartnersSection | SanityPageBuilderPointCloudSection | SanityPageBuilderProductRowSection | SanityPageBuilderShopGridSection | SanityPageBuilderShopProductSection | SanityPageBuilderShopHeroSection | SanityPageBuilderShopStatementSection | SanityPageBuilderProductSpecsSection | SanityPageBuilderRgbSection | SanityPageBuilderScrollFeaturesSection | SanityPageBuilderScrollHeadlinesSection | SanityPageBuilderScrollSpecsSection | SanityPageBuilderSensorFlowGraphicsSection | SanityPageBuilderSpecsListSection | SanityPageBuilderSpecsRowSection | SanityPageBuilderTechnicalComparisonSection | SanityPageBuilderTestimonialCarouselSection | SanityPageBuilderTextFactsMediaSection | SanityPageBuilderTextMedia12Section | SanityPageBuilderTextMedia13Section | SanityPageBuilderTextMedia13BSection | SanityPageBuilderTextMediaCSection | SanityPageBuilderToolCardsSection | SanityPageBuilderTwoColumnMediaSection | SanityPageBuilderHeadlineSection | SanityPageBuilderHeadlineBSection | SanityPageBuilderHeadlineDSection | SanityPageBuilderIntroHeadlineSection | SanityPageBuilderIntroHeadlineBSection | SanityPageBuilderHighlightHeadlineSection | SanityPageBuilderHighlightsSection | SanityPageBuilderHighlightHeadlinesSection | SanityPageBuilderLinkListSection | SanityPageBuilderLinkBarsSection | SanityPageBuilderInfoCardsSection | SanityPageBuilderEditorialCardsSection | SanityPageBuilderLegacyEditorialCardsArticleCardSection | SanityPageBuilderDistributorsSection | SanityPageBuilderExternalProductsSection | SanityPageBuilderHighlightedProductsSection | SanityPageBuilderHardwareComparisonTableSection | SanityPageBuilderHardwareComparisonTableV2Section | SanityPageBuilderIframeSection | SanityPageBuilderIntroDetailsSection | SanityPageBuilderMarketoEmbedFormSection | SanityPageBuilderMarketoFormSection | SanityPageBuilderCustomerIoFormSection | SanityPageBuilderOusterMediaGridSection | SanityPageBuilderHighlightedCardsSection | SanityPageBuilderLogoSliderSection | SanityPageBuilderLogoWallSection | SanityPageBuilderSpacerSection | SanityPageBuilderSocialShareSection | SanityPageBuilderDataFromRealWorldSection | SanityPageBuilderStatsIconsSection | SanityPageBuilderMediaSwipeSection;
export type SanityPageBuilderSection = {
  _key?: string;
  _type?: string;
  sectionMeta?: SanityPageBuilderSectionMeta;
  sectionSettings?: SanityPageBuilderSectionSettings;
  sectionContent?: SanityPageBuilderSectionContent;
};
export type SanityPageBuilder = {
  sectionsArray?: SanityPageBuilderSection[];
};
export type SanitySimpleHero = {
  _type: "simpleHero";
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
  layout?: "full" | "1/3";
  mediaLayout?: "full" | "large" | "medium";
};
export type SanityPageHero = {
  _type: "pageHero";
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
  layout?: "default" | "centered";
};
export type SanityEditorialHero = {
  _type: "editorialHero";
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
};
export type SanityHeroLabelValueRow = {
  label?: string;
  value?: string;
};
export type SanityComplexHeroBottom = {
  _type: "complexHeroBottom";
  title?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
};
export type SanityComplexHero = {
  _type: "complexHero";
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
  articles?: SanityReferenceSummary[];
  bottom?: SanityComplexHeroBottom[];
};
export type SanitySecondaryHero = {
  _type: "secondaryHero";
  eyebrow?: string;
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
  supportLink?: SanityLinkValue;
  articles?: SanityReferenceSummary[];
  widgetImage?: SanityMediaValue;
  widgetDate?: string;
  widgetTitle?: string;
  widgetUrl?: string;
};
export type SanityScrollableHeroHeadline = {
  _type: "scrollableHeroHeadline";
  topDistance?: number;
  bottomDistance?: number;
  headline?: string;
};
export type SanityScrollableHero = {
  _type: "scrollableHero";
  eyebrow?: string;
  headline?: string;
  subline?: SanityRichTextValue;
  link?: SanityLinkValue;
  media?: SanityMediaValue;
  label?: string;
  hasOverlay?: boolean;
  headlines?: SanityScrollableHeroHeadline[];
};
export type SanityCubeMode = "light" | "dark";
export type SanityCubeExperienceFeature = {
  _type: "cubeExperienceFeature";
  unlockAt?: number;
  title?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
  link?: SanityLinkValue;
};
export type SanityCubeExperienceHero = {
  _type: "cubeExperienceHero";
  eyebrow?: string;
  headline?: string;
  subline?: SanityRichTextValue;
  link?: SanityLinkValue;
  unlockedEyebrow?: string;
  unlockedHeadline?: string;
  unlockedSubline?: SanityRichTextValue;
  unlockedLink?: SanityLinkValue;
  mode?: SanityCubeMode;
  users?: SanityHeroLabelValueRow[];
  features?: SanityCubeExperienceFeature[];
};
export type SanityPageBuilderCubeExperienceSection = {
  _type: "ouster.pageBuilder.section.cubeExperience";
  eyebrow?: string;
  headline?: string;
  subline?: SanityRichTextValue;
  link?: SanityLinkValue;
  unlockedEyebrow?: string;
  unlockedHeadline?: string;
  unlockedSubline?: SanityRichTextValue;
  unlockedLink?: SanityLinkValue;
  mode?: SanityCubeMode;
  users?: SanityHeroLabelValueRow[];
  features?: SanityCubeExperienceFeature[];
};
export type SanityProductHeroTableRow = {
  label?: string;
  value?: string;
};

/** A "Specs / Stat" row of the 3.0 Sensor Hero (3.0-only field). */
export type SanityProductHeroStat = {
  _key?: string;
  chip?: string;
  label?: string;
  value?: string;
};
export type SanityProductHeroLink = {
  _type: "productHeroLink";
  link?: SanityLinkValue;
};
export type SanityProductHeroMediaItem = {
  _type: "productHeroMediaItem";
  media?: SanityMediaValue;
};
export type SanityProductHeroMedia = {
  _type: "productHeroMedia";
  titleSize?: "default" | "large";
  media?: SanityMediaValue;
  links?: SanityProductHeroLink[];
  headline?: string;
  subline?: SanityRichTextValue;
  description?: SanityRichTextValue;
  /** Design system 3.0 only: the Sensor Hero's caption, over the product caption. */
  heroCaption?: string;
  link?: SanityLinkValue;
  specs?: SanityProductHeroTableRow[];
  button?: SanityLinkValue;
  supportLink?: SanityLinkValue;
  accordions?: SanityProductHeroTableRow[];
  mediaItems?: SanityProductHeroMediaItem[];
  /** Design system 3.0 only. */
  stats?: SanityProductHeroStat[];
};
export type SanityProductHeroScrollableVideo = {
  _type: "productHeroScrollableVideo";
  headline?: string;
  frames?: SanityMediaValue[];
  bottomLeftText?: string;
  bottomRightText?: SanityRichTextValue;
  bottomRightLink?: SanityLinkValue;
  links?: SanityProductHeroLink[];
};
export type SanityConfiguratorSensor = "os-dome" | "os0" | "os0-rgb" | "os1" | "os1-max" | "os1-max-rgb";
export type SanitySensorConfiguratorHero = {
  _type: "sensorConfiguratorHero";
  configuratorSensor?: SanityConfiguratorSensor;
  link?: SanityLinkValue;
};
export type SanityRev8Hero = {
  _type: "rev8Hero";
  _key?: string;
  logo?: SanityMediaValue;
  headline?: string;
  subline?: SanityRichTextValue;
  media?: SanityMediaValue;
  /** Design system 3.0 only: black layer over the media, in % opacity. */
  mediaShade?: number;
  /** Design system 3.0 only: slides after `media`; with any, the hero is a gallery. */
  gallery?: SanityRev8HeroGallerySlide[];
  link?: SanityLinkValue;
  supportLink?: SanityLinkValue;
};

/** One extra slide of the 3.0 homepage / rev-8 hero gallery (image or video). */
export type SanityRev8HeroGallerySlide = {
  _key: string;
  _type?: "rev8HeroGallerySlide";
  media?: SanityMediaValue;
  /** Small image in the thumbnail row; the media's image or video cover when empty. */
  thumbnail?: SanityMediaValue;
};
export type SanitySdkDownloadsCopyCard = {
  _type: "sdkDownloadsCopyCard";
  title?: string;
  description?: string;
  command?: string;
  buttonLabel?: string;
};
export type SanitySdkDownloadsLinkCard = {
  _type: "sdkDownloadsLinkCard";
  title?: string;
  description?: string;
  link?: SanityLinkValue;
};
export type SanitySdkDownloadsCard = SanitySdkDownloadsCopyCard | SanitySdkDownloadsLinkCard;
export type SanitySdkDownloadsHero = {
  _type: "sdkDownloadsHero";
  media?: SanityMediaValue;
  headline?: string;
  subline?: SanityRichTextValue;
  cards?: SanitySdkDownloadsCard[];
};
export type SanityHero = SanityComplexHero | SanitySecondaryHero | SanitySimpleHero | SanityPageHero | SanityEditorialHero | SanityProductHeroMedia | SanityProductHeroScrollableVideo | SanityScrollableHero | SanityRev8Hero | SanitySensorConfiguratorHero | SanitySdkDownloadsHero;
export type SanityArticleAuthor = SanityReferenceSummary & {
  _type?: "author";
  picture?: SanityMediaValue;
};
export type SanityArticleMetadata = {
  articleAuthor?: SanityArticleAuthor;
  articleCategory?: SanityReferenceSummary;
  industryCategory?: SanityReferenceSummary;
  relatedArticles?: SanityReferenceSummary[];
  excludeFromBlog?: boolean;
  postDate?: string;
  dateUpdated?: string;
  articleLegacyImage?: SanityMediaValue;
};

/**
 * Lightweight article shape for blog listing cards (blog overview grid, hero,
 * highlighted articles, `/api/get-articles`). Mirrors the featured-gallery
 * referenced-document idiom: first hero entry carries the card text + media,
 * `articleLegacyImage` is the media fallback for pre-hero articles.
 */
export type SanityArticleCardHero = {
  _key?: string;
  _type?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
};
export type SanityArticleCard = {
  _id?: string;
  _type?: "article";
  title?: string;
  headline?: string;
  uri?: string;
  href?: string;
  hero?: SanityArticleCardHero[];
  postDate?: string;
  excludeFromBlog?: boolean;
  category?: SanityReferenceSummary;
  industry?: SanityReferenceSummary;
  articleLegacyImage?: SanityMediaValue;
};
export type SanityEventMetadata = {
  city?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  hideTime?: boolean;
  hideEndDate?: boolean;
  timezoneLabel?: string;
  boothNumber?: string;
  moreInfo?: string;
};
export type SanityWebinarMetadata = {
  webinarDate?: string;
  linkText?: string;
  watchMedia?: SanityMediaValue;
  watchText?: SanityRichTextValue;
};
export type SanityCaseStudyDetail = {
  label?: string;
  text?: string;
};
export type SanityCaseStudyLocation = {
  _key?: string;
  _type?: "caseStudyLocation";
  city?: string;
  state?: string;
  country?: string;
  lat?: number;
  lng?: number;
};

/**
 * Lightweight case-study shape for the Case Studies overview and its paginated
 * API. Card copy and media come from the first hero entry; industries and map
 * coordinates are retained for the listing's filters, tags, and map.
 */
export type SanityCaseStudyCardHero = {
  _key?: string;
  _type?: string;
  text?: SanityRichTextValue;
  media?: SanityMediaValue;
};
export type SanityCaseStudyCard = {
  _id?: string;
  _type?: "caseStudy";
  title?: string;
  headline?: string;
  uri?: string;
  href?: string;
  hero?: SanityCaseStudyCardHero[];
  postDate?: string;
  industries?: SanityReferenceSummary[];
  locations?: SanityCaseStudyLocation[];
};
export type SanityCaseStudyMetadata = {
  introHeadline?: string;
  introText?: SanityRichTextValue;
  details?: SanityCaseStudyDetail[];
  industries?: SanityReferenceSummary[];
  hardwareProducts?: SanityReferenceSummary[];
  locations?: SanityCaseStudyLocation[];
  showRelatedCaseStudies?: boolean;
  relatedCaseStudies?: SanityReferenceSummary[];
  showSocialShare?: boolean;
  postDate?: string;
  dateUpdated?: string;
};
export type SanityProductAddOn = {
  _key?: string;
  _type?: "productAddOn";
  image?: SanityMediaValue;
  name?: string;
  text?: string;
};
export type SanityProductGeneration = {
  _id?: string;
  _type: "productGeneration";
  title?: string;
  slug?: {
    current?: string;
  };
};
export type SanityHardwareMetadata = {
  productCaption?: string;
  downloadSpecsLink?: string;
  productImage?: SanityMediaValue;
  /** 3.0 product cards: cut-out render on transparent, falling back to `productImage`. */
  productRender?: SanityMediaValue;
  productHighlights?: string[];
  productAddOns?: SanityProductAddOn[];
  productGeneration?: SanityProductGeneration;
};
export type SanitySoftwareMetadata = {
  productCaption?: string;
  productImage?: SanityMediaValue;
  productIsFree?: boolean;
};
export type SanityRouteDocument = SanityRouteSettings & {
  _id?: string;
  _type?: SanityRoutableDocumentType;
  title?: string;
  uri?: string;
  href?: string;
  hero?: SanityHero[];
  pageBuilder?: SanityPageBuilder;
  seoMetadata?: SanitySeoMetadata;
  articleMetadata?: SanityArticleMetadata;
  eventMetadata?: SanityEventMetadata;
  webinarMetadata?: SanityWebinarMetadata;
  caseStudyMetadata?: SanityCaseStudyMetadata;
  hardwareMetadata?: SanityHardwareMetadata;
  softwareMetadata?: SanitySoftwareMetadata;
  // Blog-overview curation fields (`page` documents; only rendered on the Blog overview page).
  featuredArticle?: SanityArticleCard | null;
  highlightedArticlesHeadline?: string | null;
  highlightedArticles?: SanityArticleCard[] | null;
};

// -------- Products (new) schema — server projections --------

export type SanityProductsNewRefSummary = {
  _id?: string;
  title?: string;
  slug?: string;
};
export type SanityProductsNewFirmwareSummary = SanityProductsNewRefSummary & {
  image?: string;
  sha256?: string;
  userManual?: string[];
  changelog?: string[];
  apiGuide?: string[];
};
export type SanityProductsNewDatasheet = {
  _key?: string;
  firmware?: SanityProductsNewRefSummary;
  pdf?: string;
};
export type SanityProductsNewAccessoryRef = SanityProductsNewRefSummary & {
  pdf?: string;
};
export type SanityProductsNewProduct = {
  _key?: string;
  product?: string;
  partNumber?: string;
  datasheets?: SanityProductsNewDatasheet[];
  hardwareUserManual?: string[];
  sensorCadZip?: string;
  accessoryCadZip?: string;
  hardwareAccessories?: SanityProductsNewAccessoryRef[];
};
export type SanityProductsNewFirmwareWarning = {
  _key?: string;
  firmware?: SanityProductsNewRefSummary;
  message?: SanityRichTextValue;
};
export type SanityProductsNewRev = SanityProductsNewRefSummary & {
  firmwares?: SanityProductsNewRefSummary[];
  firmwareWarnings?: SanityProductsNewFirmwareWarning[];
  guides?: SanityProductsNewRefSummary[];
  products?: SanityProductsNewProduct[];
};
export type SanityProductsNewGuide = SanityProductsNewRefSummary & {
  urls?: string[];
};
export type SanityProductsNewAccessory = SanityProductsNewRefSummary & {
  pdf?: string;
};
export type SanityProductsNewGeneralSectionLink = {
  _key?: string;
  label?: string;
  url?: string;
};
export type SanityProductsNewGeneralSectionRow = {
  _key?: string;
  name?: string;
  links?: SanityProductsNewGeneralSectionLink[];
};
export type SanityProductsNewGeneralSection = {
  _id?: string;
  title?: string;
  rows?: SanityProductsNewGeneralSectionRow[];
};
export type SanityProductsNewData = {
  revs?: SanityProductsNewRev[];
  firmwares?: SanityProductsNewFirmwareSummary[];
  guides?: SanityProductsNewGuide[];
  accessories?: SanityProductsNewAccessory[];
  sections?: SanityProductsNewGeneralSection[];
};
export const sanityWildLinkProjection = `{
  _type,
  kind,
  customText,
  "download": select(kind == "file" => coalesce(canDownload, false), true => false),
  "newWindow": openInNewTab,
  "href": select(
    kind == "internal" => coalesce(
      select(
        defined(internal.sectionTarget) && defined(internal.link->pageBuilder.sectionsArray) => select(
          internal.link->uri.current == "/" => "/" + "#" + coalesce(
            internal.link->pageBuilder.sectionsArray[_key == ^.internal.sectionTarget][0].sectionSettings.sectionId.current,
            internal.link->pageBuilder.sectionsArray[_key == ^.internal.sectionTarget][0]._key
          ),
          true => "/" + internal.link->uri.current + "#" + coalesce(
            internal.link->pageBuilder.sectionsArray[_key == ^.internal.sectionTarget][0].sectionSettings.sectionId.current,
            internal.link->pageBuilder.sectionsArray[_key == ^.internal.sectionTarget][0]._key
          )
        ),
        internal.link->uri.current == "/" => "/",
        defined(internal.link->uri.current) => "/" + internal.link->uri.current,
        defined(internal.link->slug.current) => "/" + internal.link->slug.current,
        true => ""
      ),
      ""
    ),
    kind == "external" => coalesce(external, ""),
    kind == "email" => "mailto:" + coalesce(email, ""),
    kind == "phone" => "tel:" + coalesce(phone, ""),
    kind == "file" => coalesce(file.asset->url, ""),
    true => ""
  ),
  "text": coalesce(
    customText,
    select(
      kind == "internal" => coalesce(
        select(
          defined(internal.sectionTarget) && defined(internal.link->pageBuilder.sectionsArray) => coalesce(
            internal.link->pageBuilder.sectionsArray[_key == ^.internal.sectionTarget][0].sectionSettings.sectionTitle,
            internal.link->title
          ),
          true => internal.link->title
        ),
        internal.link->uri.current,
        internal.link->slug.current,
        ""
      ),
      kind == "external" => coalesce(external, ""),
      kind == "email" => coalesce(email, ""),
      kind == "phone" => coalesce(phone, ""),
      kind == "file" => coalesce(file.asset->originalFilename, ""),
      true => ""
    ),
    ""
  )
}`;
export const sanityReferenceSummaryProjection = `{
  _id,
  _type,
  title,
  "uri": uri.current,
  "slug": slug.current
}`;
export const sanityOfficeProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  logo,
  locations[]
}`;
export const sanityPartnerProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  url,
  logo,
  intro,
  "regions": o::refSummaries(regions),
  "countries": o::refSummaries(countries),
  "services": o::refSummaries(services),
  "industry": o::refSummary(industry)
}`;
export const sanityImageProjection = `{
  "_id": asset->_id,
  "url": asset->url,
  "mimeType": asset->mimeType,
  "originalFilename": asset->originalFilename,
  "crop": crop,
  "hotspot": hotspot,
  "title": asset->title,
  "altText": asset->altText,
  "lqip": asset->metadata.lqip,
  "description": asset->description,
  "dimensions": asset->metadata.dimensions{width, height, aspectRatio}
}`;
export const sanityAirImageProjection = `{
  "_id": coalesce(versionId, assetId),
  "provider": "air",
  "url": coalesce(
    urls.selected,
    select(versionReference == "dynamic" => urls.dynamic),
    urls.static,
    urls.dynamic
  ),
  "mimeType": select(
    lower(name) match "*.svg" => "image/svg+xml",
    lower(name) match "*.gif" => "image/gif",
    lower(name) match "*.avif" => "image/avif",
    lower(name) match "*.png" => "image/png",
    lower(name) match "*.webp" => "image/webp",
    lower(name) match "*.jpg" || lower(name) match "*.jpeg" => "image/jpeg",
    "image/*"
  ),
  "originalFilename": name,
  "title": name,
  "altText": alt,
  description,
  "dimensions": {
    width,
    height,
    "aspectRatio": coalesce(aspectRatio, width / height)
  }
}`;
export const sanitySelectableImageProjection = `{
  ...select(
    source == "air" && defined(air.assetId) => o::airImage(air),
    source == "sanity" && defined(imageFile.asset) => o::image(imageFile),
    !defined(source) && defined(imageFile.asset) => o::image(imageFile),
    !defined(source) && defined(air.assetId) => o::airImage(air)
  )
}`;
export const sanityVideoProjection = `{
  ...select(
    kind == "file" => {
      "kind": "file",
      "options": videoOptions,
      "cover": o::selectableImage(videoCover),
      "source": {
        "provider": "sanity",
        "delivery": "file",
        "src": coalesce(file.videoFile.asset->url, videoFile.asset->url),
        "mimeType": coalesce(file.videoFile.asset->mimeType, videoFile.asset->mimeType),
        "dimensions": { "width": 1920, "height": 1080, "aspectRatio": 16 / 9 }
      }
    },
    kind == "air" => {
      "kind": "air",
      "options": videoOptions,
      "cover": select(
        (
          videoCover.source == "sanity" && defined(videoCover.imageFile.asset)
        ) || (
          videoCover.source == "air" && defined(videoCover.air.assetId)
        ) || (
          !defined(videoCover.source) && defined(videoCover.imageFile.asset)
        ) || (
          !defined(videoCover.source) &&
          !defined(videoCover.imageFile.asset) &&
          defined(videoCover.air.assetId)
        ) => o::selectableImage(videoCover),
        defined(air.urls.thumbnail) => {
          "_id": coalesce(air.versionId, air.assetId),
          "provider": "air",
          "url": air.urls.thumbnail,
          "mimeType": "image/*",
          "originalFilename": air.name,
          "title": air.name,
          "altText": air.alt,
          "description": air.description,
          "dimensions": {
            "width": air.width,
            "height": air.height,
            "aspectRatio": coalesce(air.aspectRatio, air.width / air.height)
          }
        }
      ),
      "source": {
        "provider": "air",
        "delivery": "hls",
        "src": coalesce(
          air.urls.selected,
          select(air.versionReference == "dynamic" => air.urls.dynamic),
          air.urls.static,
          air.urls.dynamic
        ),
        "mimeType": "application/vnd.apple.mpegurl",
        "dimensions": {
          "width": air.width,
          "height": air.height,
          "aspectRatio": coalesce(air.aspectRatio, air.width / air.height)
        }
      }
    },
    kind == "youtube" => {
      "kind": "youtube",
      "options": videoOptions,
      "cover": o::selectableImage(videoCover),
      "source": {
        "src": youtube.videoUrl,
        "dimensions": { "width": 1920, "height": 1080, "aspectRatio": 16 / 9 }
      }
    },
    kind == "vimeo" => {
      "kind": "vimeo",
      "options": videoOptions,
      "cover": o::selectableImage(videoCover),
      "source": {
        "src": vimeo.videoUrl,
        "dimensions": { "width": 1920, "height": 1080, "aspectRatio": 16 / 9 }
      }
    },
    kind == "mux" => {
      "kind": "mux",
      "options": videoOptions,
      "cover": o::selectableImage(videoCover),
      "source": {
        "src": select(
          defined(mux.playbackId) && mux.playbackId != "" => mux.playbackId,
          mux.videoSrc.asset->playbackId
        ),
        "duration": mux.videoSrc.asset->data.duration,
        "dimensions": mux.videoSrc.asset->data.tracks[0]{
          "width": max_width,
          "height": max_height,
          "aspectRatio": max_width / max_height
        }
      }
    },
    true => null
  )
}`;
export const sanityMotionProjection = `{
  ...select(
    kind == "rive" => {
      "kind": "rive",
      "options": rive.riveOptions,
      "source": rive.riveFile.asset->{
        "src": url,
        "mimeType": mimeType,
        "dimensions": { width, height, aspectRatio }
      }
    },
    kind == "lottie" => {
      "kind": "lottie",
      "options": lottie.lottieOptions,
      "source": lottie.lottieFile.asset->{
        "src": url,
        "mimeType": mimeType,
        "dimensions": { width, height, aspectRatio }
      }
    },
    true => null
  )
}`;
export const sanityMediaProjection = `{
  ...select(
    kind == "image" && defined(image) => {
      _type,
      caption,
      aspectRatio,
      isDarkened,
      "kind": "image",
      "image": { "source": o::selectableImage(image) }
    },
    kind == "video" && defined(video) => {
      _type,
      caption,
      aspectRatio,
      "kind": "video",
      "video": o::video(video)
    },
    kind == "motion" && defined(motion) => {
      _type,
      caption,
      aspectRatio,
      "kind": "motion",
      "motion": o::motion(motion)
    },
    true => null
  )
}`;
export const sanityPageBuilderMediaItemProjection = `{
  _key,
  _type,
  "media": o::media(media)
}`;
export const sanityRichTextInlineProjection = `{
  ...,
  markDefs[]{
    ...,
    _type == "wild.richtext.annotation.link" => o::link(@)
  },
  children[]{
    ...,
    _type == "wild.richtext.span.media" => {
      "media": o::media(media)
    }
  }
}`;
export const sanityRichTextProjection = `[]{
  ...,
  markDefs[]{
    ...,
    _type == "wild.richtext.annotation.link" => o::link(@)
  },
  children[]{
    ...,
    _type == "wild.richtext.span.media" => {
      "media": o::media(media)
    }
  },
  _type == "wild.richtext.block.quote" => {
    _type,
    _key,
    "quoteText": o::richTextInline(quote.quote),
    "author": {
      "name": coalesce(quote.authorRef->title, quote.authorRef->name, quote.authorName),
      "description": select(
        defined(quote.authorRef) => quote.authorRef->description,
        true => quote.authorDescription
      )
    }
  },
  _type == "wild.richtext.block.mediaOnly" => {
    _type,
    _key,
    "media": o::media(media)
  },
  _type == "wild.richtext.block.mediaGrid" => {
    _type,
    _key,
    "items": o::mediaItem(items)
  },
  _type == "wild.richtext.block.mediaSlider" => {
    _type,
    _key,
    "items": o::mediaItem(items)
  }
}`;

/**
 * Custom GROQ function declarations for the leaf projections above.
 *
 * GROQ has no reuse primitive in plain query text, so every `${leaf}`
 * interpolation used to be inlined at every call site. That is combinatorial:
 * the media projection alone appeared 378 times inside the page-builder
 * section projection, expanding it to ~7.5 MB — far past the API CDN's
 * 307,200-byte POST limit, and slow to parse.
 *
 * Sanity's custom GROQ functions (https://www.sanity.io/docs/content-lake/custom-groq-functions)
 * let each leaf be declared once and called everywhere. The declarations below
 * reuse the exported projection strings verbatim, so the projections remain
 * the single source of truth and stay usable inline.
 *
 * Body forms are chosen to match how each leaf is called:
 * - `$p{...}`    — a single object (`media`, `link`, `videoCover`, …)
 * - `$p[]{...}`  — an array field (`items`, rich-text blocks, …)
 * - `$p->{...}`  — a single reference; `$p[]->{...}` an array of references
 *
 * The array/reference variants matter: the server distributes a `$p{...}` body
 * over an array argument, but `groq-js` (used by the Studio) does not, and a
 * `$p[]{...}` body fed an already-dereferenced `field[]->` argument yields
 * nulls on the server. Matching the body form to the argument keeps both
 * engines byte-identical to the previous inline output.
 *
 * Declaration order follows the dependency chain; a function body may call any
 * other function declared in the same preamble.
 */
export const sanityGroqFunctionsPreamble = [`fn o::image($p) = $p${sanityImageProjection};`, `fn o::airImage($p) = $p${sanityAirImageProjection};`, `fn o::selectableImage($p) = $p${sanitySelectableImageProjection};`, `fn o::video($p) = $p${sanityVideoProjection};`, `fn o::motion($p) = $p${sanityMotionProjection};`, `fn o::media($p) = $p${sanityMediaProjection};`, `fn o::medias($p) = $p[]{ ...o::media(@) };`, `fn o::link($p) = $p${sanityWildLinkProjection};`, `fn o::refSummary($p) = $p->${sanityReferenceSummaryProjection};`, `fn o::refSummaries($p) = $p[]->${sanityReferenceSummaryProjection};`, `fn o::mediaItem($p) = $p[]${sanityPageBuilderMediaItemProjection};`, `fn o::richTextInline($p) = $p[]${sanityRichTextInlineProjection};`, `fn o::richText($p) = $p${sanityRichTextProjection};`].join("\n");

/**
 * Prepend {@link sanityGroqFunctionsPreamble} to a query that calls any `o::`
 * function. Idempotent: declaring the same function twice is a server error,
 * so a query that already carries the preamble is returned unchanged.
 *
 * CAUTION when composing: this guard only protects against re-wrapping the
 * SAME string. Interpolating an already-wrapped query (any exported `*Query`)
 * into a larger query embeds `fn` declarations mid-body, which is a GROQ
 * syntax error the idempotency check cannot save you from. Compose from the
 * `*Projection` fragments instead and wrap the final query once.
 */
export function withSanityGroqFunctions(query: string): string {
  return query.includes("fn o::") ? query : `${sanityGroqFunctionsPreamble}\n${query}`;
}

/** Adds the preamble only to queries that actually call an `o::` function. */
export function withSanityGroqFunctionsIfNeeded(query: string): string {
  return query.includes("o::") ? withSanityGroqFunctions(query) : query;
}

/**
 * Inverse of {@link withSanityGroqFunctions}: removes the preamble prefix so a
 * query can be SERIALIZED without carrying ~9.5 KB of `fn` declarations (e.g.
 * live-preview queries embedded per section in Next page props). Exact
 * round-trip guarantee: `withSanityGroqFunctions(stripSanityGroqFunctions(q))`
 * returns `q` byte-for-byte for any query produced by the wrapper. Consumers
 * MUST re-join before executing the query — `withSanityGroqFunctionsIfNeeded`
 * is safe on both stripped and never-wrapped strings.
 */
export function stripSanityGroqFunctions(query: string): string {
  return query.startsWith(`${sanityGroqFunctionsPreamble}\n`) ? query.slice(sanityGroqFunctionsPreamble.length + 1) : query;
}

// Reference-document projections used by the data-API page-builder section
// loaders (partners / distributors / officesMap). They resolve `logo` through
// the media projection (so the web media bridge can read a URL) and dereference
// the category references the listing cards/filters display.
export const sanityOfficeCardProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  "logo": o::media(logo),
  locations[]
}`;
export const sanityPartnerCardProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  url,
  "logo": o::media(logo),
  intro,
  "regions": o::refSummaries(regions),
  "countries": o::refSummaries(countries),
  "services": o::refSummaries(services),
  "industry": o::refSummary(industry)
}`;
export const sanityDistributorCardProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  url,
  email,
  "logo": o::media(logo),
  intro,
  "industry": o::refSummary(industry),
  "regions": o::refSummaries(regions),
  locations[]{ _key, _type, country, state, lat, lng }
}`;
export const sanityNavigationLinkProjection = `{
  _key,
  _type,
  label,
  "link": o::link(link)
}`;
export const sanityFooterLinkProjection = `{
  _key,
  _type,
  label,
  "link": o::link(link)
}`;
export const sanityNavigationQuickLinksProjection = `{
  _key,
  _type,
  title,
  links[] ${sanityNavigationLinkProjection}
}`;
export const sanityNavigationProductGroupProjection = `{
  _key,
  _type,
  title,
  links[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationQuickLinks" => ${sanityNavigationQuickLinksProjection}
  }
}`;
export const sanityNavigationDropdownProjection = `{
  _key,
  _type,
  title,
  items[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationProductGroup" => ${sanityNavigationProductGroupProjection},
    _type == "navigationQuickLinks" => ${sanityNavigationQuickLinksProjection}
  }
}`;
export const sanityNavigationAdditionalDropdownProjection = `{
  _key,
  _type,
  title,
  links[] ${sanityNavigationLinkProjection}
}`;
export const sanityNavigationDrawerLinkProjection = `{
  _key,
  _type,
  label,
  size,
  "link": o::link(link)
}`;
export const sanityNavigationDrawerDropdownProjection = `{
  _key,
  _type,
  title,
  links[] ${sanityNavigationDrawerLinkProjection}
}`;
export const sanityFooterGroupProjection = `{
  _key,
  _type,
  title,
  links[] ${sanityFooterLinkProjection}
}`;
export const sanityFooterSocialProjection = `{
  _key,
  _type,
  type,
  url
}`;
export const sanitySeoMetadataProjection = `seoMetadata{
  noIndex,
  title,
  description,
  "image": {
    "asset": select(
      imageSource == "air" && defined(airImage.assetId) => o::airImage(airImage),
      imageSource == "sanity" && defined(image.asset) => o::image(image),
      !defined(imageSource) && defined(image.asset) => o::image(image),
      !defined(imageSource) && defined(airImage.assetId) => o::airImage(airImage)
    )
  }
}`;
export const sanityNavigationSettingsProjection = `{
  _id,
  _type,
  "extraNavLogo": o::media(extraNavLogo),
  "extraNavLogoNegative": o::media(extraNavLogoNegative),
  "extraNavLogoLink": o::link(extraNavLogoLink),
  navLinks[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationDropdown" => ${sanityNavigationDropdownProjection},
    _type == "navigationQuickLinks" => ${sanityNavigationQuickLinksProjection}
  },
  navAdditionalLinks[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationAdditionalDropdown" => ${sanityNavigationAdditionalDropdownProjection}
  },
  navDrawerLinks[]{
    _type == "navigationDrawerLink" => ${sanityNavigationDrawerLinkProjection},
    _type == "navigationDrawerDropdown" => ${sanityNavigationDrawerDropdownProjection}
  },
  navDrawerAdditionalLinks[] ${sanityNavigationLinkProjection}
}`;
export const sanityFooterSettingsProjection = `{
  _id,
  _type,
  "footerLink": o::link(footerLink),
  footerLinks[] ${sanityFooterGroupProjection},
  footerAdditionalLinks[] ${sanityNavigationLinkProjection},
  footerBottomLinks[] ${sanityNavigationLinkProjection},
  footerSocials[] ${sanityFooterSocialProjection},
  newsletterText
}`;
export const sanitySeoSettingsProjection = `{
  _id,
  _type,
  ${sanitySeoMetadataProjection}
}`;
export const sanityAnnouncementSettingsProjection = `{
  _id,
  _type,
  announcementBarText
}`;
export const sanityLlmsSettingsProjection = `{
  _id,
  _type,
  title,
  description,
  groups[]{
    _key,
    heading,
    isOptional,
    links[]{
      _key,
      title,
      href,
      note,
      "uri": reference->uri.current
    }
  }
}`;
export const sanityNavigationSettingsQuery = withSanityGroqFunctionsIfNeeded(`*[_id == "navigationSettings"][0]${sanityNavigationSettingsProjection}`);
export const sanityFooterSettingsQuery = withSanityGroqFunctionsIfNeeded(`*[_id == "footerSettings"][0]${sanityFooterSettingsProjection}`);
export const sanitySeoSettingsQuery = withSanityGroqFunctionsIfNeeded(`*[_id == "seoSettings"][0]${sanitySeoSettingsProjection}`);
export const sanityAnnouncementSettingsQuery = withSanityGroqFunctionsIfNeeded(`*[_id == "announcementSettings"][0]${sanityAnnouncementSettingsProjection}`);
/**
 * Every routable document's `uri` and `_id`, for the published route table.
 *
 * Route resolution used to ask Content Lake "is there a document at this URL?"
 * once per requested URL, keying the query cache on the URL itself. That key
 * space is unbounded — a crawler walking unique paths never reuses an entry, so
 * every request was a guaranteed miss and a 15,836-byte round trip, including
 * for URLs that match nothing at all. Route lookups were 69% of all web Sanity
 * queries (82,792 of 119,584 over 2026-09-14..20).
 *
 * This query is 153 bytes, needs no `o::` preamble, and returns ~25 KB for the
 * whole site (290 documents). One cached entry replaces the unbounded key
 * space: a URL absent from the table resolves to a 404 with NO Sanity request,
 * and a URL present in it fetches by `_id`, a bounded key space. See
 * vault/sanity.caching.md.
 */
export const sanityRouteTableQuery = withSanityGroqFunctionsIfNeeded(`*[_type in [${sanityRouteDocumentTypeFilter}] && defined(uri.current)]{"uri": uri.current, _id}`);

/** One row of {@link sanityRouteTableQuery}. */
export type SanityRouteTableEntry = {
  _id: string;
  uri: string;
};

/**
 * The four settings singletons in ONE request, for published renders.
 *
 * Each separate query above is independently wrapped with the `o::` preamble,
 * so fetching them individually sends ~9.5 KB of identical `fn` declarations
 * three times over (31,040 bytes total vs 12,157 combined — an 18,883-byte
 * saving per render, on top of three fewer billable API requests).
 *
 * This matters because published renders are dominated by eager ISR
 * regeneration after a publish: a settings edit invalidates every page, so the
 * per-render query count is a direct multiplier on the invalidation cost. See
 * vault/sanity.caching.md.
 *
 * Preview/draft renders deliberately keep the four separate queries: the
 * Presentation tool subscribes per `{query, params}` pair, and a merged result
 * has no single re-runnable query behind it (see `toSanityLiveSiteSettings`).
 */
export const sanitySiteSettingsCombinedQuery = withSanityGroqFunctions(`{
  "navigation": *[_id == "navigationSettings"][0]${sanityNavigationSettingsProjection},
  "footer": *[_id == "footerSettings"][0]${sanityFooterSettingsProjection},
  "seo": *[_id == "seoSettings"][0]${sanitySeoSettingsProjection},
  "announcement": *[_id == "announcementSettings"][0]${sanityAnnouncementSettingsProjection}
}`);

/** Result shape of {@link sanitySiteSettingsCombinedQuery}. */
export type SanitySiteSettingsCombined = {
  announcement: SanityAnnouncementSettings | null;
  footer: SanityFooterSettings | null;
  navigation: SanityNavigationSettings | null;
  seo: SanitySeoSettings | null;
};
export const sanityLlmsSettingsQuery = withSanityGroqFunctionsIfNeeded(`*[_id == "llmsSettings"][0]${sanityLlmsSettingsProjection}`);
export const sanityRouteSettingsProjection = `{
  navTheme,
  pagePattern,
  footerPattern,
  pageMode,
  designVersion,
  "customLogo": o::media(customLogo),
  isHighlighted,
  hideFooter,
  backButton,
  pageNavAdditionalLinks[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationAdditionalDropdown" => ${sanityNavigationAdditionalDropdownProjection}
  },
  "supportLink": o::link(supportLink),
  supportLinkVariant,
  subNavLinks[] ${sanityNavigationLinkProjection},
  subNavMode,
  popup,
  enableImageParallax
}`;
export const sanityPageBuilderSectionSettingsProjection = `{
  sectionTitle,
  sectionId,
  anchorName,
  hasTopDivider,
  mode,
  theme,
  spacingBelow
}`;
export const sanityPageBuilderSectionMetaProjection = `{
  sectionKind,
  isLibrarySection,
  isShareableSection
}`;
export const sanityRequestQuoteHardwareProductProjection = `{
  _id,
  _type,
  title,
  "uri": uri.current,
  productCaption,
  "productImage": o::media(productImage),
  productAddOns[]{
    _key,
    _type,
    "image": o::media(image),
    name,
    text
  }
}`;
export const sanityRequestQuoteSoftwareProductProjection = `{
  _id,
  _type,
  title,
  "uri": uri.current,
  productCaption,
  "productImage": o::media(productImage),
  productIsFree
}`;
export const sanityRequestQuoteSettingsProjection = `{
  _id,
  _type,
  show,
  text,
  headline,
  "body": o::richText(body),
  marketoFormId,
  hardwareProducts[]->${sanityRequestQuoteHardwareProductProjection},
  softwareProducts[]->${sanityRequestQuoteSoftwareProductProjection},
  successTitle,
  "successBody": o::richText(successBody),
  "successImage": o::media(successImage),
  privacyCheckboxLabel,
  marketingCheckboxLabel
}`;

/**
 * Loads both split quote dialogs in one round trip. Each rev falls back to the
 * generic `requestQuoteSettings` document when its revision-specific settings
 * are unavailable.
 */
export const sanityRequestQuoteGlobalsQuery = withSanityGroqFunctionsIfNeeded(`{
  "rev7": coalesce(
    *[_id == "requestQuoteRev7Settings"][0]${sanityRequestQuoteSettingsProjection},
    *[_id == "requestQuoteSettings"][0]${sanityRequestQuoteSettingsProjection}
  ),
  "rev8": coalesce(
    *[_id == "requestQuoteRev8Settings"][0]${sanityRequestQuoteSettingsProjection},
    *[_id == "requestQuoteSettings"][0]${sanityRequestQuoteSettingsProjection}
  )
}`);
export const sanityPageBuilderTextOnlySectionProjection = `{
  _type,
  "text": o::richText(text),
  layout
}`;
export const sanityPageBuilderMediaOnlySectionProjection = `{
  _type,
  "media": o::media(media),
  layout,
  assetLayout,
  useOriginalRatio,
  isHighlighted,
  hasSolidBackground,
  tag,
  headline,
  text,
  "link": o::link(link),
  hideDesktopNotch
}`;
export const sanityPageBuilderMediaSliderSectionProjection = `{
  _type,
  "items": o::mediaItem(items)
}`;
export const sanityPageBuilderQuoteSectionProjection = `{
  _type,
  "quoteText": o::richText(quote.quote),
  "author": {
    "name": coalesce(quote.authorRef->title, quote.authorRef->name, quote.authorName),
    "description": select(
      defined(quote.authorRef) => quote.authorRef->description,
      true => quote.authorDescription
    )
  }
}`;
export const sanityPageBuilderAccordionListItemBulletPointProjection = `{
  _key,
  _type,
  title,
  "text": o::richText(text)
}`;
export const sanityPageBuilderAccordionListItemProjection = `{
  _key,
  _type,
  title,
  shortDescription,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media),
  bulletPoints[] ${sanityPageBuilderAccordionListItemBulletPointProjection}
}`;
export const sanityPageBuilderAccordionListSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderAccordionListItemProjection}
}`;
export const sanityPageBuilderBannerSectionProjection = `{
  _type,
  tag,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media),
  isHighlighted
}`;
export const sanityPageBuilderBannerNotesBackgroundProjection = `{
  _key,
  _type,
  "media": o::media(media)
}`;
export const sanityPageBuilderBannerNotesNoteProjection = `{
  _key,
  _type,
  topic,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media)
}`;
export const sanityPageBuilderBannerNotesSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  linkText,
  "media": o::media(media),
  backgrounds[] ${sanityPageBuilderBannerNotesBackgroundProjection},
  notes[] ${sanityPageBuilderBannerNotesNoteProjection}
}`;
export const sanityPageBuilderBenefitsBenefitProjection = `{
  _key,
  _type,
  title,
  text
}`;
export const sanityPageBuilderBenefitsSectionProjection = `{
  _type,
  benefits[] ${sanityPageBuilderBenefitsBenefitProjection}
}`;
export const sanityPageBuilderBenefitListItemProjection = `{
  _key,
  _type,
  title,
  "text": o::richText(text)
}`;
export const sanityPageBuilderBenefitsListSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderBenefitListItemProjection}
}`;
export const sanityPageBuilderCallToActionListItemProjection = `{
  _key,
  _type,
  headline,
  text,
  "link": o::link(link),
  "image": o::media(image)
}`;
export const sanityPageBuilderCallToActionListSectionProjection = `{
  _type,
  showLinks,
  items[] ${sanityPageBuilderCallToActionListItemProjection}
}`;
export const sanityPageBuilderCallToActionCardSectionProjection = `{
  _type,
  headline,
  text,
  "primaryLink": o::link(primaryLink),
  "secondaryLink": o::link(secondaryLink)
}`;
export const sanityCubeExperienceFeatureProjection = `{
  _key,
  _type,
  unlockAt,
  title,
  text,
  "media": o::media(media),
  "link": o::link(link)
}`;
export const sanityPageBuilderCubeExperienceSectionProjection = `{
  _type,
  eyebrow,
  headline,
  subline,
  "link": o::link(link),
  unlockedEyebrow,
  unlockedHeadline,
  unlockedSubline,
  "unlockedLink": o::link(unlockedLink),
  mode,
  users[],
  features[] ${sanityCubeExperienceFeatureProjection}
}`;

// The union of product documents (revisions, firmwares, guides, accessories,
// generalDownloadSections, velodyneDownloads) shaped for the downloads UI.
// It is reused by `sanityProductsNewDataProjection` and
// `sanityPageBuilderProductDownloadsTableSectionProjection`.
export const sanityProductsNewDataBodyProjection = `{
  "revs": *[_type == "productRev"] | order(orderRank asc) {
    _id, title, "slug": slug.current,
    "firmwares": firmwares[]->{_id, title, "slug": slug.current},
    "firmwareWarnings": firmwareWarnings[]{
      _key,
      "firmware": firmware->{_id, title, "slug": slug.current},
      message
    },
    "guides": guides[]->{_id, title, "slug": slug.current},
    products[]{
      _key, product, partNumber,
      "datasheets": datasheets[]{
        _key,
        "firmware": firmware->{_id, title, "slug": slug.current},
        pdf
      },
      hardwareUserManual,
      sensorCadZip,
      accessoryCadZip,
      "hardwareAccessories": hardwareAccessories[]->{_id, title, pdf}
    }
  },
  "firmwares": *[_type == "productFirmware"] | order(order asc) {
    _id, title, "slug": slug.current, image, sha256, userManual, changelog, apiGuide
  },
  "guides": *[_type == "productGuide"] | order(orderRank asc) {
    _id, title, "slug": slug.current, urls
  },
  "accessories": *[_type == "productAccessory"] | order(orderRank asc) {
    _id, title, "slug": slug.current, pdf
  },
  "sections": *[_type == "generalDownloadSection"] | order(orderRank asc) {
    _id, title, rows[]{_key, name, links[]{_key, label, url}}
  }
}`;

// Renders the picker + tables from Products (new). The productsNewData
// aggregation is fetched inline via nested `*[...]` so the section is
// self-contained and can be authored on any page. The section itself has no
// editable fields — everything is derived from the Products (new) docs.
export const sanityPageBuilderProductDownloadsTableSectionProjection = `{
  _type,
  "productsNewData": ${sanityProductsNewDataBodyProjection}
}`;
export const sanityPageBuilderChipEvolutionGraphBulletPointProjection = `{
  _key,
  _type,
  text
}`;
export const sanityPageBuilderChipEvolutionGraphItemProjection = `{
  _key,
  _type,
  "image": o::media(image),
  name,
  year,
  range,
  pointsPerSecond
}`;
export const sanityPageBuilderChipEvolutionGraphSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  bulletPoints[] ${sanityPageBuilderChipEvolutionGraphBulletPointProjection},
  showXAxisData,
  items[] ${sanityPageBuilderChipEvolutionGraphItemProjection},
  linkText,
  "link": o::link(link)
}`;
export const sanityPageBuilderContentGallerySpecProjection = `{
  _key,
  _type,
  label,
  value
}`;
export const sanityPageBuilderContentGalleryItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  "description": o::richText(description),
  specs[] ${sanityPageBuilderContentGallerySpecProjection},
  "link": o::link(link)
}`;
export const sanityPageBuilderContentGallerySectionProjection = `{
  _type,
  isHighlighted,
  hasCounters,
  items[] ${sanityPageBuilderContentGalleryItemProjection}
}`;
export const sanityPageBuilderFeaturedGalleryReferencedDocumentProjection = `{
  _id,
  _type,
  title,
  "headline": title,
  "uri": uri.current,
  "href": select(
    uri.current == "/" => "/",
    string::startsWith(uri.current, "/") => uri.current,
    defined(uri.current) => "/" + uri.current,
    null
  ),
  "url": select(
    uri.current == "/" => "/",
    string::startsWith(uri.current, "/") => uri.current,
    defined(uri.current) => "/" + uri.current,
    null
  ),
  hero[]{
    _key,
    _type,
    "text": o::richText(text),
    "media": o::media(media)
  },
  "postDate": select(_type == "article" => postDate, _type == "caseStudy" => postDate),
  "date": select(_type == "webinar" => webinarDate),
  "linkText": select(_type == "webinar" => linkText),
  "category": select(_type == "article" => o::refSummary(articleCategory)),
  "industry": select(
    _type == "article" => o::refSummary(industryCategory),
    _type == "caseStudy" => o::refSummaries(industries)
  )
}`;
export const sanityPageBuilderFeaturedGalleryGeneralCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  hasCounter,
  title,
  "text": o::richText(text),
  "link": o::link(link)
}`;
export const sanityPageBuilderFeaturedGallerySectionProjection = `{
  _type,
  items[]{
    _type == "featuredGalleryGeneralCard" => ${sanityPageBuilderFeaturedGalleryGeneralCardProjection},
    _type == "featuredGalleryArticleCard" => {
      _key,
      _type,
      article->${sanityPageBuilderFeaturedGalleryReferencedDocumentProjection}
    },
    _type == "featuredGalleryCaseStudyCard" => {
      _key,
      _type,
      caseStudy->${sanityPageBuilderFeaturedGalleryReferencedDocumentProjection}
    },
    _type == "featuredGalleryWebinarCard" => {
      _key,
      _type,
      webinar->${sanityPageBuilderFeaturedGalleryReferencedDocumentProjection}
    }
  }
}`;
export const sanityPageBuilderCoreFeaturesGalleryItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  "link": o::link(link),
  title,
  "description": o::richText(description)
}`;
export const sanityPageBuilderCoreFeaturesGallerySectionProjection = `{
  _type,
  items[] ${sanityPageBuilderCoreFeaturesGalleryItemProjection}
}`;
export const sanityPageBuilderCoreVideoFeaturesGalleryItemProjection = `{
  _key,
  _type,
  headline,
  "description": o::richText(description),
  "media": o::media(media)
}`;
export const sanityPageBuilderCoreVideoFeaturesGallerySectionProjection = `{
  _type,
  headline,
  isHighlighted,
  items[] ${sanityPageBuilderCoreVideoFeaturesGalleryItemProjection}
}`;
export const sanityPageBuilderFaqQuestionProjection = `{
  _key,
  _type,
  question,
  "questionSlug": questionSlug.current,
  "answer": o::richText(answer)
}`;
export const sanityPageBuilderFaqSectionProjection = `{
  _type,
  headline,
  "link": o::link(link),
  questions[] ${sanityPageBuilderFaqQuestionProjection}
}`;
export const sanityPageBuilderDeepDiveGalleryStepProjection = `{
  _key,
  _type,
  headline,
  "text": o::richText(text),
  "image": o::media(image),
  "video": o::media(video),
  videoStart,
  videoEnd,
  isIntro,
  hasBlur
}`;
export const sanityPageBuilderDeepDiveGallerySectionProjection = `{
  _type,
  hasOverlay,
  "video": o::media(video),
  steps[] ${sanityPageBuilderDeepDiveGalleryStepProjection}
}`;
export const sanityPageBuilderFeaturesComparisonTableItemProjection = `{
  _key,
  _type,
  feature,
  featuredIn[]
}`;
export const sanityPageBuilderFeaturesComparisonTableSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderFeaturesComparisonTableItemProjection}
}`;
export const sanityPageBuilderGallerySlideProjection = `{
  _key,
  _type,
  title,
  "media": o::media(media),
  contentPlacement,
  "text": o::richText(text),
  label,
  "link": o::link(link),
  "supportLink": o::link(supportLink)
}`;
export const sanityPageBuilderGallerySectionProjection = `{
  _type,
  headline,
  eyebrow,
  "text": o::richText(text),
  layout,
  hasInsetDividers,
  isHighlighted,
  slides[] ${sanityPageBuilderGallerySlideProjection}
}`;
export const sanityPageBuilderGraphicStatsStatProjection = `{
  _key,
  _type,
  stat
}`;
export const sanityPageBuilderStatsGraphicBarProjection = `{
  _key,
  _type,
  name,
  value,
  highlighted,
  tag
}`;
export const sanityPageBuilderGraphicStatsSectionProjection = `{
  _type,
  headline,
  caption,
  callToActionText,
  "link": o::link(link),
  max,
  graphic[] ${sanityPageBuilderStatsGraphicBarProjection},
  stats[] ${sanityPageBuilderGraphicStatsStatProjection}
}`;
export const sanityPageBuilderGraphicCardsCardProjection = `{
  _key,
  _type,
  "image": o::media(image),
  title,
  "text": o::richText(text)
}`;
export const sanityPageBuilderGraphicCardsSectionProjection = `{
  _type,
  cards[] ${sanityPageBuilderGraphicCardsCardProjection}
}`;
export const sanityPageBuilderGeneralGridCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  "text": o::richText(text),
  "link": o::link(link)
}`;
export const sanityPageBuilderGridCardsSectionProjection = `{
  _type,
  cards[] ${sanityPageBuilderGeneralGridCardProjection}
}`;
export const sanityPageBuilderMediaCardsCardLinkProjection = `{
  _key,
  _type,
  "link": o::link(link)
}`;
export const sanityPageBuilderMediaCardsCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  headline,
  "text": o::richText(text),
  links[] ${sanityPageBuilderMediaCardsCardLinkProjection}
}`;
export const sanityPageBuilderMediaCardsSectionProjection = `{
  _type,
  cards[] ${sanityPageBuilderMediaCardsCardProjection}
}`;
export const sanityPageBuilderPartnerGalleryItemProjection = `{
  _key,
  _type,
  "logo": o::media(logo),
  "media": o::media(media),
  "link": o::link(link),
  title,
  "description": o::richText(description)
}`;
export const sanityPageBuilderPartnerGallerySectionProjection = `{
  _type,
  eyebrow,
  headline,
  "backgroundMedia": o::media(backgroundMedia),
  isHighlighted,
  showGalleryNavigation,
  items[] ${sanityPageBuilderPartnerGalleryItemProjection}
}`;
export const sanityPageBuilderStackedGallerySpecProjection = `{
  _key,
  _type,
  label,
  value
}`;
export const sanityPageBuilderStackedGalleryItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  category,
  "cta": o::link(cta),
  title,
  "description": o::richText(description),
  "link": o::link(link),
  specs[] ${sanityPageBuilderStackedGallerySpecProjection}
}`;
export const sanityPageBuilderStackedGallerySectionProjection = `{
  _type,
  items[] ${sanityPageBuilderStackedGalleryItemProjection}
}`;
export const sanityPageBuilderMediaCarouselSlideProjection = `{
  _key,
  _type,
  "media": o::media(media)
}`;
export const sanityPageBuilderMediaCarouselSectionProjection = `{
  _type,
  slides[] ${sanityPageBuilderMediaCarouselSlideProjection}
}`;
export const sanityPageBuilderMediaHighlightsItemProjection = `{
  _key,
  _type,
  left,
  right,
  value
}`;
export const sanityPageBuilderMediaHighlightsSectionProjection = `{
  _type,
  variant,
  isHighlighted,
  hasSolidBackground,
  disableImageParallax,
  "media": o::media(media),
  headline,
  eyebrow,
  title,
  "link": o::link(link),
  highlights[] ${sanityPageBuilderMediaHighlightsItemProjection}
}`;
export const sanityPageBuilderMediaWithHighlightsItemProjection = `{
  _key,
  _type,
  label,
  text
}`;
export const sanityPageBuilderMediaWithHighlightsSectionProjection = `{
  _type,
  "media": o::media(media),
  hasCounters,
  highlights[] ${sanityPageBuilderMediaWithHighlightsItemProjection}
}`;
export const sanityPageBuilderNewsletterSectionProjection = `{
  _type,
  headline
}`;
export const sanityPageBuilderOfficesMapSectionProjection = `{
  _type
}`;
export const sanityPageBuilderOpenJobPositionsSectionProjection = `{
  _type,
  successStepTitle,
  successStepDescription,
  "successStepMedia": o::media(successStepMedia)
}`;
export const sanityPageBuilderPartnersSectionProjection = `{
  _type
}`;
export const sanityPageBuilderPointCloudSectionProjection = `{
  _type,
  headline,
  minZoom,
  maxZoom,
  "media": o::media(media),
  topMedia {
    "media": o::media(media)
  },
  bottomMedia {
    "media": o::media(media),
    scale
  }
}`;
export const sanityHardwareCardProjection = `{
  _id,
  _type,
  title,
  "uri": uri.current,
  productCaption,
  downloadSpecsLink,
  "productImage": o::media(productImage),
  "productRender": o::media(productRender),
  productGeneration->{_id, _type, title, slug},
  productHighlights,
  specValues[]{_key, _type, specKey, value}
}`;
export const sanityPageBuilderProductRowSectionProjection = `{
  _type,
  products[]->${sanityHardwareCardProjection}
}`;
export const sanityMerchCardProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  price,
  soldOut,
  sizeOptions,
  "image": o::media(images[0])
}`;
export const sanityPageBuilderShopGridSectionProjection = `{
  _type,
  products[]->${sanityMerchCardProjection}
}`;
export const sanityMerchProductProjection = `{
  _id,
  _type,
  title,
  "slug": slug.current,
  price,
  "description": o::richText(description),
  "image": o::media(images[0]),
  sizeOptions,
  specs[]{_key, label, value},
  "details": o::richText(details),
  "materials": o::richText(materials),
  limitNote,
  soldOut
}`;
export const sanityPageBuilderShopProductSectionProjection = `{
  _type,
  product->${sanityMerchProductProjection}
}`;
export const sanityPageBuilderShopHeroSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text)
}`;
export const sanityPageBuilderShopStatementSectionProjection = `{
  _type,
  headline
}`;
export const sanityPageBuilderProductSpecsItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  spec,
  title,
  "description": o::richText(description)
}`;
export const sanityPageBuilderProductSpecsSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderProductSpecsItemProjection}
}`;
export const sanityPageBuilderRgbItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  headline,
  "text": o::richText(text),
  "link": o::link(link)
}`;
export const sanityPageBuilderRgbSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  "media": o::media(media),
  slides[] ${sanityPageBuilderRgbItemProjection}
}`;
export const sanityPageBuilderScrollFeaturesFeatureProjection = `{
  _key,
  _type,
  headline,
  "media": o::media(media)
}`;
export const sanityPageBuilderScrollFeaturesSectionProjection = `{
  _type,
  features[] ${sanityPageBuilderScrollFeaturesFeatureProjection}
}`;
export const sanityPageBuilderScrollHeadlinesHeadlineProjection = `{
  _key,
  _type,
  topDistance,
  bottomDistance,
  headline
}`;
export const sanityPageBuilderScrollHeadlinesSectionProjection = `{
  _type,
  topDistance,
  startVisible,
  "video": o::media(video),
  label,
  hasOverlay,
  headlines[] ${sanityPageBuilderScrollHeadlinesHeadlineProjection}
}`;
export const sanityPageBuilderScrollSpecsSpecProjection = `{
  _key,
  _type,
  title,
  text
}`;
export const sanityPageBuilderScrollSpecsSectionProjection = `{
  _type,
  headline,
  "media": o::media(media),
  specs[] ${sanityPageBuilderScrollSpecsSpecProjection}
}`;
export const sanityPageBuilderSensorFlowGraphicsGraphicProjection = `{
  _key,
  _type,
  headline,
  leadingText,
  union,
  trailingText,
  hasHorizontalLayout
}`;
export const sanityPageBuilderSensorFlowGraphicsSectionProjection = `{
  _type,
  headline,
  "media": o::media(media),
  graphics[] ${sanityPageBuilderSensorFlowGraphicsGraphicProjection}
}`;
export const sanityPageBuilderSpecsListSpecProjection = `{
  _key,
  _type,
  title,
  subTitle,
  "image": o::media(image)
}`;
export const sanityPageBuilderSpecsListSectionProjection = `{
  _type,
  specs[] ${sanityPageBuilderSpecsListSpecProjection},
  "link": o::link(link),
  isHighlighted,
  layout,
  columns,
  hasBorder
}`;
export const sanityPageBuilderSpecsRowSpecProjection = `{
  _key,
  _type,
  name,
  value
}`;
export const sanityPageBuilderSpecsRowSectionProjection = `{
  _type,
  specs[] ${sanityPageBuilderSpecsRowSpecProjection}
}`;
export const sanityPageBuilderTechnicalComparisonSpecProjection = `{
  _key,
  _type,
  label,
  value
}`;
export const sanityPageBuilderTechnicalComparisonLinkProjection = `{
  _key,
  _type,
  "link": o::link(link),
  linkIcon
}`;
export const sanityPageBuilderTechnicalComparisonSpecGroupProjection = `{
  _key,
  _type,
  title,
  specs[] ${sanityPageBuilderTechnicalComparisonSpecProjection}
}`;
export const sanityPageBuilderTechnicalComparisonItemProjection = `{
  _key,
  _type,
  "productImage": o::media(productImage),
  "illustration": o::media(illustration),
  "productLineArt": o::media(productLineArt),
  "drawerImage": o::media(drawerImage),
  title,
  specs[] ${sanityPageBuilderTechnicalComparisonSpecProjection},
  "link": o::link(link),
  "supportLink": o::link(supportLink),
  links[] ${sanityPageBuilderTechnicalComparisonLinkProjection},
  specGroups[] ${sanityPageBuilderTechnicalComparisonSpecGroupProjection},
  "specSheet": o::link(specSheet),
  isSelected,
  "specSheetFileSize": select(specSheet.kind == "file" => specSheet.file.asset->size)
}`;
export const sanityPageBuilderTechnicalComparisonSectionProjection = `{
  _type,
  isHighlighted,
  items[] ${sanityPageBuilderTechnicalComparisonItemProjection}
}`;
export const sanityPageBuilderTestimonialsCarouselSlideProjection = `{
  _key,
  _type,
  quote,
  "media": o::media(media),
  author
}`;
export const sanityPageBuilderTestimonialCarouselSectionProjection = `{
  _type,
  slides[] ${sanityPageBuilderTestimonialsCarouselSlideProjection},
  slideshowInterval,
  hasSolidBackground
}`;
export const sanityPageBuilderTextFactsMediaFactProjection = `{
  _key,
  _type,
  title,
  label
}`;
export const sanityPageBuilderTextFactsMediaSectionProjection = `{
  _type,
  headline,
  eyebrow,
  "media": o::media(media),
  facts[] ${sanityPageBuilderTextFactsMediaFactProjection}
}`;
export const sanityPageBuilderTextMedia12SectionProjection = `{
  _type,
  headline,
  hasSpacer,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media),
  mediaPosition,
  eyebrow,
  background,
  "secondaryMedia": o::media(secondaryMedia)
}`;
export const sanityPageBuilderTextMedia13SectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media),
  mediaPosition
}`;
export const sanityPageBuilderTextMedia13BSectionProjection = `{
  _type,
  headline,
  "media": o::media(media),
  mediaPosition
}`;
export const sanityPageBuilderTextMediaCItemProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  "description": o::richText(description)
}`;
export const sanityPageBuilderTextMediaCSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  "media": o::media(media),
  mediaPosition,
  items[] ${sanityPageBuilderTextMediaCItemProjection}
}`;
export const sanityPageBuilderToolCardsCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  "description": o::richText(description),
  url
}`;
export const sanityPageBuilderToolCardsSectionProjection = `{
  _type,
  headline,
  isHighlighted,
  cards[] ${sanityPageBuilderToolCardsCardProjection}
}`;
export const sanityPageBuilderTwoColumnMediaColumnProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  description,
  "link": o::link(link)
}`;
export const sanityPageBuilderTwoColumnMediaSectionProjection = `{
  _type,
  medias[] ${sanityPageBuilderTwoColumnMediaColumnProjection},
  background
}`;
export const sanityPageBuilderHeadlineSectionProjection = `{
  _type,
  headline,
  eyebrow,
  "text": o::richText(text),
  "link": o::link(link),
  linkVariant,
  isHighlighted,
  highlight,
  hasSolidBackground,
  variant
}`;
export const sanityPageBuilderHeadlineBSectionProjection = `{
  _type,
  headline,
  eyebrow,
  "text": o::richText(text),
  "link": o::link(link),
  linkVariant,
  isHighlighted,
  hasSpacer,
  hasSolidBackground,
  variant
}`;
export const sanityPageBuilderHeadlineDSectionProjection = `{
  _type,
  headline,
  eyebrow,
  "link": o::link(link),
  linkVariant,
  isHighlighted,
  hasSpacer,
  hasSolidBackground
}`;
export const sanityPageBuilderIntroHeadlineSectionProjection = `{
  _type,
  headline,
  hasSpacer,
  "text": o::richText(text),
  "link": o::link(link),
  "media": o::media(media),
  mediaPosition,
  hasSolidBackground,
  "logo": o::media(logo)
}`;
export const sanityPageBuilderIntroHeadlineBSectionProjection = `{
  _type,
  eyebrow,
  headline,
  headlineAccent,
  "text": o::richText(text),
  "media": o::media(media),
  mediaPosition,
  "primaryLink": o::link(primaryLink),
  "secondaryLink": o::link(secondaryLink)
}`;
export const sanityPageBuilderHighlightHeadlineSectionProjection = `{
  _type,
  "image": o::media(image),
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  isHighlighted,
  hasScrollHighlighting,
  isBig,
  hasSolidBackground,
  variant
}`;
export const sanityPageBuilderHighlightsHighlightProjection = `{
  _key,
  _type,
  title,
  "text": o::richText(text),
  "link": o::link(link)
}`;
export const sanityPageBuilderHighlightsSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  hasCounters,
  highlights[] ${sanityPageBuilderHighlightsHighlightProjection},
  hasFooter,
  footerTitle,
  "footerLogo": o::media(footerLogo)
}`;
export const sanityPageBuilderHighlightHeadlinesItemProjection = `{
  _key,
  _type,
  "image": o::media(image),
  headline,
  "text": o::richText(text)
}`;
export const sanityPageBuilderHighlightHeadlinesSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderHighlightHeadlinesItemProjection}
}`;
export const sanityPageBuilderLinkListItemProjection = `{
  _key,
  _type,
  title,
  "link": o::link(link)
}`;
export const sanityPageBuilderLinkListSectionProjection = `{
  _type,
  headline,
  items[] ${sanityPageBuilderLinkListItemProjection}
}`;
export const sanityPageBuilderLinkBarsLinkProjection = `{
  _key,
  _type,
  "link": o::link(link),
  caption
}`;
export const sanityPageBuilderLinkBarsItemProjection = `{
  _key,
  _type,
  title,
  links[] ${sanityPageBuilderLinkBarsLinkProjection}
}`;
export const sanityPageBuilderLinkBarsSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderLinkBarsItemProjection}
}`;
export const sanityPageBuilderInfoCardsCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  title,
  "description": o::richText(description),
  url
}`;
export const sanityPageBuilderInfoCardsSectionProjection = `{
  _type,
  headline,
  isHighlighted,
  cards[] ${sanityPageBuilderInfoCardsCardProjection}
}`;

// --- Editorial Cards ---
export const sanityPageBuilderEditorialCardsReferencedDocumentProjection = sanityPageBuilderFeaturedGalleryReferencedDocumentProjection;
export const sanityPageBuilderEditorialCardsInlineCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  hasCounter,
  title,
  "text": o::richText(text),
  "link": o::link(link)
}`;
export const sanityPageBuilderEditorialCardsSectionProjection = `{
  _type,
  eyebrow,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  hasFilter,
  isHighlighted,
  cards[]{
    _type == "editorialCardsGeneralCard" => ${sanityPageBuilderEditorialCardsInlineCardProjection},
    _type == "editorialCardsShowcaseCard" => ${sanityPageBuilderEditorialCardsInlineCardProjection},
    _type == "editorialCardsArticleCard" => {
      _key,
      _type,
      article->${sanityPageBuilderEditorialCardsReferencedDocumentProjection}
    },
    _type == "editorialCardsCaseStudyCard" => {
      _key,
      _type,
      caseStudy->${sanityPageBuilderEditorialCardsReferencedDocumentProjection}
    },
    _type == "editorialCardsWebinarCard" => {
      _key,
      _type,
      webinar->${sanityPageBuilderEditorialCardsReferencedDocumentProjection}
    }
  }
}`;
export const sanityPageBuilderLegacyEditorialCardsArticleCardSectionProjection = `{
  _key,
  _type,
  article->${sanityPageBuilderEditorialCardsReferencedDocumentProjection}
}`;

// --- Reference collections ---
export const sanityPageBuilderDistributorsSectionProjection = `{
  _type,
  "industries": o::refSummaries(industries)
}`;
export const sanityPageBuilderExternalProductsCardProjection = `{
  _key,
  _type,
  title,
  "description": o::richText(description),
  "media": o::media(media),
  url
}`;
export const sanityPageBuilderExternalProductsSectionProjection = `{
  _type,
  eyebrow,
  headline,
  "text": o::richText(text),
  cards[] ${sanityPageBuilderExternalProductsCardProjection}
}`;
export const sanityPageBuilderHighlightedProductsSectionProjection = `{
  _type,
  headline,
  text,
  products[]->${sanityHardwareCardProjection},
  "link": o::link(link),
  isHighlighted,
  hasFilter
}`;

// --- Hardware comparison tables ---
export const sanityHardwareComparisonSpecProjection = `{
  _key,
  _type,
  label,
  icon,
  "customIcon": o::media(customIcon)
}`;
export const sanityPageBuilderHardwareComparisonTableSectionProjection = `{
  _type,
  hasHighlightDifferencesToggle,
  specs[] ${sanityHardwareComparisonSpecProjection},
  products[]->${sanityHardwareCardProjection}
}`;
export const sanityPageBuilderHardwareComparisonTableV2SectionProjection = `{
  _type,
  hasHighlightDifferencesToggle,
  highlightInstructions,
  "productPreviewImage": o::media(productPreviewImage),
  specs[] ${sanityHardwareComparisonSpecProjection},
  products[]->${sanityHardwareCardProjection}
}`;

// --- Embeds ---
export const sanityPageBuilderIframeSectionProjection = `{
  _type,
  url,
  title,
  mobileDisclaimerText,
  usabilityDisclaimerText,
  layout
}`;
export const sanityPageBuilderIntroDetailsItemProjection = `{
  _key,
  _type,
  label,
  text,
  baseLink
}`;
export const sanityPageBuilderIntroDetailsSectionProjection = `{
  _type,
  headline,
  "text": o::richText(text),
  details[] ${sanityPageBuilderIntroDetailsItemProjection}
}`;
export const sanityPageBuilderMarketoEmbedFormSectionProjection = `{
  _type,
  headline,
  description,
  marketoFormId,
  marketoFormUrl,
  marketoFormInstanceId,
  "customCss": o::richText(customCss),
  successMessageTitle,
  "successMessageDescription": o::richText(successMessageDescription),
  "successMessageImage": o::media(successMessageImage),
  hasRedirect,
  redirectUrl,
  fullWidth
}`;
export const sanityPageBuilderMarketoFormSectionProjection = `{
  _type,
  marketoFormId,
  layout,
  successMessage,
  "successMessageText": o::richText(successMessageText),
  "successMessageImage": o::media(successMessageImage),
  "disclaimer": o::richText(disclaimer),
  privacyCheckboxLabel,
  showWatchWebinarButton
}`;
export const sanityPageBuilderCustomerIoFormSectionProjection = `{
  _type,
  headline,
  description,
  formName,
  fields[]{
    _key,
    _type,
    key,
    label,
    type,
    placeholder,
    required,
    options,
    destination
  },
  layout,
  submitLabel,
  successMessage,
  "successMessageText": o::richText(successMessageText),
  "successMessageImage": o::media(successMessageImage),
  "disclaimer": o::richText(disclaimer),
  privacyCheckboxLabel,
  event->{
    _id,
    title,
    "eventName": eventName.current,
    description
  },
  eventMetadata[]{
    _key,
    key,
    value
  }
}`;

// --- Media grid family ---
export const sanityPageBuilderMediaGridDecorationProjection = `{
  _key,
  _type,
  "image": o::media(image),
  left,
  top,
  width,
  parallax,
  glass,
  glassBlur,
  mobileLeft,
  mobileTop,
  mobileWidth
}`;
export const sanityPageBuilderMediaGridContentImageCardProjection = `{
  _key,
  _type,
  "media": o::media(media),
  mediaShade,
  mediaShadeGradient,
  decorations[] ${sanityPageBuilderMediaGridDecorationProjection},
  eyebrow,
  "logo": o::media(logo),
  title,
  "description": o::richText(description),
  "link": o::link(link),
  "supportLink": o::link(supportLink)
}`;
export const sanityPageBuilderMediaGridContentRowProjection = `{
  _key,
  _type,
  contentRowLayout,
  cards[] ${sanityPageBuilderMediaGridContentImageCardProjection}
}`;
export const sanityPageBuilderOusterMediaGridSectionProjection = `{
  _type,
  isHighlighted,
  hasSolidBackground,
  cardsLayout,
  rows[] ${sanityPageBuilderMediaGridContentRowProjection}
}`;
export const sanityPageBuilderHighlightedCardsItemProjection = `{
  _key,
  _type,
  title,
  "description": o::richText(description),
  "link": o::link(link),
  "logo": o::media(logo),
  "image": o::media(image)
}`;
export const sanityPageBuilderHighlightedCardsSectionProjection = `{
  _type,
  eyebrow,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  isHighlighted,
  cards[] ${sanityPageBuilderHighlightedCardsItemProjection}
}`;
export const sanityPageBuilderLogoSliderLogoProjection = `{
  _key,
  _type,
  "image": o::media(image),
  url
}`;
export const sanityPageBuilderLogoSliderSectionProjection = `{
  _type,
  logos[] ${sanityPageBuilderLogoSliderLogoProjection},
  showMobileSecondRow,
  showDesktopSecondRow
}`;
export const sanityPageBuilderLogoWallSectionProjection = `{
  _type,
  isHighlighted,
  headline,
  "gallery": o::medias(gallery)
}`;
export const sanityPageBuilderSpacerSectionProjection = `{
  _type,
  isHighlighted,
  hasSolidBackground,
  isBig
}`;
export const sanityPageBuilderSocialShareSectionProjection = `{
  _type,
  text
}`;
export const sanityPageBuilderDataFromRealWorldItemProjection = `{
  _key,
  _type,
  title,
  "thumbnail": o::media(thumbnail),
  "media": o::media(media),
  "strip": o::media(strip),
  tags,
  "link": o::link(link)
}`;
export const sanityPageBuilderDataFromRealWorldSectionProjection = `{
  _type,
  eyebrow,
  headline,
  "text": o::richText(text),
  "link": o::link(link),
  items[] ${sanityPageBuilderDataFromRealWorldItemProjection}
}`;
export const sanityPageBuilderStatsIconsItemProjection = `{
  _key,
  _type,
  label,
  "icon": o::media(icon),
  "rive": rive ${sanityRiveProjection}
}`;
export const sanityPageBuilderStatsIconsSectionProjection = `{
  _type,
  items[] ${sanityPageBuilderStatsIconsItemProjection}
}`;
export const sanityPageBuilderMediaSwipeSectionProjection = `{
  _type,
  overlayHeadline,
  "media": o::media(media),
  eyebrow,
  headline,
  "text": o::richText(text),
  "link": o::link(link)
}`;

/**
 * Every page-builder `sectionContent` branch, keyed by its bare section
 * `_type`. This is the single source of truth: both the full section
 * projection and any page-scoped subset are built from it.
 */
export const sanityPageBuilderSectionContentBranches: Record<string, string> = {
  "wild.pageBuilder.section.textOnly": sanityPageBuilderTextOnlySectionProjection,
  "wild.pageBuilder.section.mediaOnly": sanityPageBuilderMediaOnlySectionProjection,
  "wild.pageBuilder.section.mediaSlider": sanityPageBuilderMediaSliderSectionProjection,
  "wild.pageBuilder.section.quote": sanityPageBuilderQuoteSectionProjection,
  "ouster.pageBuilder.section.accordionList": sanityPageBuilderAccordionListSectionProjection,
  "ouster.pageBuilder.section.banner": sanityPageBuilderBannerSectionProjection,
  "ouster.pageBuilder.section.bannerNotes": sanityPageBuilderBannerNotesSectionProjection,
  "ouster.pageBuilder.section.benefits": sanityPageBuilderBenefitsSectionProjection,
  "ouster.pageBuilder.section.benefitsList": sanityPageBuilderBenefitsListSectionProjection,
  "ouster.pageBuilder.section.callToActionList": sanityPageBuilderCallToActionListSectionProjection,
  "ouster.pageBuilder.section.cubeExperience": sanityPageBuilderCubeExperienceSectionProjection,
  "ouster.pageBuilder.section.productDownloadsTable": sanityPageBuilderProductDownloadsTableSectionProjection,
  "ouster.pageBuilder.section.chipEvolutionGraph": sanityPageBuilderChipEvolutionGraphSectionProjection,
  "ouster.pageBuilder.section.contentGallery": sanityPageBuilderContentGallerySectionProjection,
  "ouster.pageBuilder.section.featuredGallery": sanityPageBuilderFeaturedGallerySectionProjection,
  "ouster.pageBuilder.section.coreFeaturesGallery": sanityPageBuilderCoreFeaturesGallerySectionProjection,
  "ouster.pageBuilder.section.coreVideoFeaturesGallery": sanityPageBuilderCoreVideoFeaturesGallerySectionProjection,
  "ouster.pageBuilder.section.deepDiveGallery": sanityPageBuilderDeepDiveGallerySectionProjection,
  "ouster.pageBuilder.section.faq": sanityPageBuilderFaqSectionProjection,
  "ouster.pageBuilder.section.featuresComparisonTable": sanityPageBuilderFeaturesComparisonTableSectionProjection,
  "ouster.pageBuilder.section.gallery": sanityPageBuilderGallerySectionProjection,
  "ouster.pageBuilder.section.graphicStats": sanityPageBuilderGraphicStatsSectionProjection,
  "ouster.pageBuilder.section.graphicCards": sanityPageBuilderGraphicCardsSectionProjection,
  "ouster.pageBuilder.section.gridCards": sanityPageBuilderGridCardsSectionProjection,
  "ouster.pageBuilder.section.mediaCards": sanityPageBuilderMediaCardsSectionProjection,
  "ouster.pageBuilder.section.partnerGallery": sanityPageBuilderPartnerGallerySectionProjection,
  "ouster.pageBuilder.section.stackedGallery": sanityPageBuilderStackedGallerySectionProjection,
  "ouster.pageBuilder.section.mediaCarousel": sanityPageBuilderMediaCarouselSectionProjection,
  "ouster.pageBuilder.section.mediaHighlights": sanityPageBuilderMediaHighlightsSectionProjection,
  "ouster.pageBuilder.section.mediaWithHighlights": sanityPageBuilderMediaWithHighlightsSectionProjection,
  "ouster.pageBuilder.section.newsletter": sanityPageBuilderNewsletterSectionProjection,
  "ouster.pageBuilder.section.officesMap": sanityPageBuilderOfficesMapSectionProjection,
  "ouster.pageBuilder.section.openJobPositions": sanityPageBuilderOpenJobPositionsSectionProjection,
  "ouster.pageBuilder.section.partners": sanityPageBuilderPartnersSectionProjection,
  "ouster.pageBuilder.section.pointCloud": sanityPageBuilderPointCloudSectionProjection,
  "ouster.pageBuilder.section.productRow": sanityPageBuilderProductRowSectionProjection,
  "ouster.pageBuilder.section.shopGrid": sanityPageBuilderShopGridSectionProjection,
  "ouster.pageBuilder.section.shopProduct": sanityPageBuilderShopProductSectionProjection,
  "ouster.pageBuilder.section.shopHero": sanityPageBuilderShopHeroSectionProjection,
  "ouster.pageBuilder.section.shopStatement": sanityPageBuilderShopStatementSectionProjection,
  "ouster.pageBuilder.section.productSpecs": sanityPageBuilderProductSpecsSectionProjection,
  "ouster.pageBuilder.section.rgb": sanityPageBuilderRgbSectionProjection,
  "ouster.pageBuilder.section.scrollFeatures": sanityPageBuilderScrollFeaturesSectionProjection,
  "ouster.pageBuilder.section.scrollHeadlines": sanityPageBuilderScrollHeadlinesSectionProjection,
  "ouster.pageBuilder.section.scrollSpecs": sanityPageBuilderScrollSpecsSectionProjection,
  "ouster.pageBuilder.section.sensorFlowGraphics": sanityPageBuilderSensorFlowGraphicsSectionProjection,
  "ouster.pageBuilder.section.specsList": sanityPageBuilderSpecsListSectionProjection,
  "ouster.pageBuilder.section.specsRow": sanityPageBuilderSpecsRowSectionProjection,
  "ouster.pageBuilder.section.technicalComparison": sanityPageBuilderTechnicalComparisonSectionProjection,
  "ouster.pageBuilder.section.testimonialCarousel": sanityPageBuilderTestimonialCarouselSectionProjection,
  "ouster.pageBuilder.section.textFactsMedia": sanityPageBuilderTextFactsMediaSectionProjection,
  "ouster.pageBuilder.section.textMedia1_2": sanityPageBuilderTextMedia12SectionProjection,
  "ouster.pageBuilder.section.textMedia1_3": sanityPageBuilderTextMedia13SectionProjection,
  "ouster.pageBuilder.section.textMedia1_3_B": sanityPageBuilderTextMedia13BSectionProjection,
  "ouster.pageBuilder.section.textMediaC": sanityPageBuilderTextMediaCSectionProjection,
  "ouster.pageBuilder.section.toolCards": sanityPageBuilderToolCardsSectionProjection,
  "ouster.pageBuilder.section.twoColumnMedia": sanityPageBuilderTwoColumnMediaSectionProjection,
  "ouster.pageBuilder.section.headline": sanityPageBuilderHeadlineSectionProjection,
  "ouster.pageBuilder.section.headline_B": sanityPageBuilderHeadlineBSectionProjection,
  "ouster.pageBuilder.section.headline_D": sanityPageBuilderHeadlineDSectionProjection,
  "ouster.pageBuilder.section.introHeadline": sanityPageBuilderIntroHeadlineSectionProjection,
  "ouster.pageBuilder.section.introHeadline_B": sanityPageBuilderIntroHeadlineBSectionProjection,
  "ouster.pageBuilder.section.callToActionCard": sanityPageBuilderCallToActionCardSectionProjection,
  "ouster.pageBuilder.section.highlightHeadline": sanityPageBuilderHighlightHeadlineSectionProjection,
  "ouster.pageBuilder.section.highlights": sanityPageBuilderHighlightsSectionProjection,
  "ouster.pageBuilder.section.highlightHeadlines": sanityPageBuilderHighlightHeadlinesSectionProjection,
  "ouster.pageBuilder.section.linkList": sanityPageBuilderLinkListSectionProjection,
  "ouster.pageBuilder.section.linkBars": sanityPageBuilderLinkBarsSectionProjection,
  "ouster.pageBuilder.section.infoCards": sanityPageBuilderInfoCardsSectionProjection,
  "ouster.pageBuilder.section.editorialCards": sanityPageBuilderEditorialCardsSectionProjection,
  "editorialCardsArticleCard": sanityPageBuilderLegacyEditorialCardsArticleCardSectionProjection,
  "ouster.pageBuilder.section.distributors": sanityPageBuilderDistributorsSectionProjection,
  "ouster.pageBuilder.section.externalProducts": sanityPageBuilderExternalProductsSectionProjection,
  "ouster.pageBuilder.section.highlightedProducts": sanityPageBuilderHighlightedProductsSectionProjection,
  "ouster.pageBuilder.section.hardwareComparisonTable": sanityPageBuilderHardwareComparisonTableSectionProjection,
  "ouster.pageBuilder.section.hardwareComparisonTableV2": sanityPageBuilderHardwareComparisonTableV2SectionProjection,
  "ouster.pageBuilder.section.iframe": sanityPageBuilderIframeSectionProjection,
  "ouster.pageBuilder.section.introDetails": sanityPageBuilderIntroDetailsSectionProjection,
  "ouster.pageBuilder.section.marketoEmbedForm": sanityPageBuilderMarketoEmbedFormSectionProjection,
  "ouster.pageBuilder.section.marketoForm": sanityPageBuilderMarketoFormSectionProjection,
  "ouster.pageBuilder.section.customerIoForm": sanityPageBuilderCustomerIoFormSectionProjection,
  "ouster.pageBuilder.section.mediaGrid": sanityPageBuilderOusterMediaGridSectionProjection,
  "ouster.pageBuilder.section.highlightedCards": sanityPageBuilderHighlightedCardsSectionProjection,
  "ouster.pageBuilder.section.logoSlider": sanityPageBuilderLogoSliderSectionProjection,
  "ouster.pageBuilder.section.logoWall": sanityPageBuilderLogoWallSectionProjection,
  "ouster.pageBuilder.section.spacer": sanityPageBuilderSpacerSectionProjection,
  "ouster.pageBuilder.section.socialShare": sanityPageBuilderSocialShareSectionProjection,
  "ouster.pageBuilder.section.dataFromRealWorld": sanityPageBuilderDataFromRealWorldSectionProjection,
  "ouster.pageBuilder.section.statsIcons": sanityPageBuilderStatsIconsSectionProjection,
  "ouster.pageBuilder.section.mediaSwipe": sanityPageBuilderMediaSwipeSectionProjection
};

/**
 * Builds a page-builder section projection covering only `types`. Unknown
 * types are omitted, which matches how GROQ already treats a section whose
 * `_type` has no matching branch: `sectionContent` projects to `{}`.
 */
export function buildSanityPageBuilderSectionProjection(types: readonly string[]): string {
  const branches = types.map(type => {
    const projection = sanityPageBuilderSectionContentBranches[type];
    return projection ? `    _type == "${type}" => ${projection}` : undefined;
  }).filter((branch): branch is string => branch !== undefined);

  // A projection may not be empty, so with no matching branch emit a condition
  // that can never hold — `sectionContent` still resolves to `{}`.
  const sectionContent = branches.length ? branches.join(",\n") : `    _type == "" => { _type }`;
  return `{
  _key,
  _type,
  sectionMeta ${sanityPageBuilderSectionMetaProjection},
  sectionSettings ${sanityPageBuilderSectionSettingsProjection},
  sectionContent{
${sectionContent}
  }
}`;
}
export const sanityPageBuilderSectionProjection = buildSanityPageBuilderSectionProjection(Object.keys(sanityPageBuilderSectionContentBranches));
export const sanityPageBuilderProjection = `"pageBuilder": pageBuilder{
  sectionsArray[] ${sanityPageBuilderSectionProjection}
}`;
export const sanitySimpleHeroProjection = `{
  _key,
  _type,
  headline,
  subline,
  "media": o::media(media),
  "link": o::link(link),
  layout,
  mediaLayout
}`;
export const sanityPageHeroProjection = `{
  _key,
  _type,
  headline,
  subline,
  "media": o::media(media),
  "link": o::link(link),
  layout
}`;
export const sanityEditorialHeroProjection = `{
  _key,
  _type,
  headline,
  subline,
  "media": o::media(media)
}`;
export const sanityComplexHeroBottomProjection = `{
  _key,
  _type,
  title,
  text,
  "media": o::media(media),
  "link": o::link(link)
}`;
export const sanityComplexHeroProjection = `{
  _key,
  _type,
  headline,
  subline,
  "media": o::media(media),
  "articles": o::refSummaries(articles),
  bottom[] ${sanityComplexHeroBottomProjection}
}`;
export const sanitySecondaryHeroProjection = `{
  _key,
  _type,
  eyebrow,
  headline,
  subline,
  "media": o::media(media),
  "link": o::link(link),
  "supportLink": o::link(supportLink),
  "articles": o::refSummaries(articles),
  "widgetImage": o::media(widgetImage),
  widgetDate,
  widgetTitle,
  widgetUrl
}`;
export const sanityScrollableHeroHeadlineProjection = `{
  _key,
  _type,
  topDistance,
  bottomDistance,
  headline
}`;
export const sanityScrollableHeroProjection = `{
  _key,
  _type,
  eyebrow,
  headline,
  subline,
  "link": o::link(link),
  "media": o::media(media),
  label,
  hasOverlay,
  headlines[] ${sanityScrollableHeroHeadlineProjection}
}`;
export const sanityProductHeroLinkProjection = `{
  _key,
  _type,
  "link": o::link(link)
}`;
export const sanityProductHeroMediaItemProjection = `{
  _key,
  _type,
  "media": o::media(media)
}`;
export const sanityProductHeroMediaProjection = `{
  _key,
  _type,
  titleSize,
  "media": o::media(media),
  links[] ${sanityProductHeroLinkProjection},
  headline,
  subline,
  description,
  heroCaption,
  "link": o::link(link),
  specs[],
  "button": o::link(button),
  "supportLink": o::link(supportLink),
  accordions[],
  mediaItems[] ${sanityProductHeroMediaItemProjection},
  stats[]{_key, chip, label, value}
}`;
export const sanityProductHeroScrollableVideoProjection = `{
  _key,
  _type,
  headline,
  "frames": o::medias(frames),
  bottomLeftText,
  bottomRightText,
  "bottomRightLink": o::link(bottomRightLink),
  links[] ${sanityProductHeroLinkProjection}
}`;
export const sanitySensorConfiguratorHeroProjection = `{
  _key,
  _type,
  configuratorSensor,
  "link": o::link(link)
}`;
export const sanityRev8HeroProjection = `{
  _key,
  _type,
  "logo": o::media(logo),
  headline,
  subline,
  "media": o::media(media),
  mediaShade,
  "gallery": gallery[]{ _key, _type, "media": o::media(media), "thumbnail": o::media(thumbnail) },
  "link": o::link(link),
  "supportLink": o::link(supportLink)
}`;
export const sanitySdkDownloadsCopyCardProjection = `{
  _key,
  _type,
  title,
  description,
  command,
  buttonLabel
}`;
export const sanitySdkDownloadsLinkCardProjection = `{
  _key,
  _type,
  title,
  description,
  "link": o::link(link)
}`;
export const sanitySdkDownloadsHeroProjection = `{
  _key,
  _type,
  "media": o::media(media),
  headline,
  subline,
  cards[]{
    _type == "sdkDownloadsCopyCard" => ${sanitySdkDownloadsCopyCardProjection},
    _type == "sdkDownloadsLinkCard" => ${sanitySdkDownloadsLinkCardProjection}
  }
}`;
export const sanityHeroProjection = `hero[]{
  _type == "complexHero" => ${sanityComplexHeroProjection},
  _type == "secondaryHero" => ${sanitySecondaryHeroProjection},
  _type == "simpleHero" => ${sanitySimpleHeroProjection},
  _type == "pageHero" => ${sanityPageHeroProjection},
  _type == "editorialHero" => ${sanityEditorialHeroProjection},
  _type == "productHeroMedia" => ${sanityProductHeroMediaProjection},
  _type == "productHeroScrollableVideo" => ${sanityProductHeroScrollableVideoProjection},
  _type == "scrollableHero" => ${sanityScrollableHeroProjection},
  _type == "rev8Hero" => ${sanityRev8HeroProjection},
  _type == "sensorConfiguratorHero" => ${sanitySensorConfiguratorHeroProjection},
  _type == "sdkDownloadsHero" => ${sanitySdkDownloadsHeroProjection}
}`;

/** GROQ filter for articles that belong in the blog listing. */
export const sanityBlogArticleFilter = `_type == "article" && excludeFromBlog != true`;

/**
 * Projection for `SanityArticleCard`. Article heroes store their card copy in
 * `subline` (post `repair:hero-text-fields`), so `text` coalesces both fields
 * to match the featured-gallery referenced-document idiom.
 */
export const sanityArticleCardProjection = `{
  _id,
  _type,
  title,
  "headline": title,
  "uri": uri.current,
  "href": select(
    uri.current == "/" => "/",
    string::startsWith(uri.current, "/") => uri.current,
    defined(uri.current) => "/" + uri.current,
    null
  ),
  hero[]{
    _key,
    _type,
    "text": o::richText(coalesce(text, subline)),
    "media": o::media(media)
  },
  postDate,
  excludeFromBlog,
  "category": o::refSummary(articleCategory),
  "industry": o::refSummary(industryCategory),
  "articleLegacyImage": o::media(articleLegacyImage)
}`;

/** GROQ filter for routable case studies displayed by the overview listing. */
export const sanityCaseStudyFilter = `_type == "caseStudy" && defined(uri.current)`;

/**
 * Projection for `SanityCaseStudyCard`. Mirrors the article card projection:
 * card copy is normalized to `text` from the first hero's legacy/current text
 * fields, while locations keep the overview map functional.
 */
export const sanityCaseStudyCardProjection = `{
  _id,
  _type,
  title,
  "headline": title,
  "uri": uri.current,
  "href": select(
    uri.current == "/" => "/",
    string::startsWith(uri.current, "/") => uri.current,
    defined(uri.current) => "/" + uri.current,
    null
  ),
  hero[]{
    _key,
    _type,
    "text": o::richText(coalesce(text, subline)),
    "media": o::media(media)
  },
  postDate,
  "industries": o::refSummaries(industries),
  locations[]{
    _key,
    _type,
    city,
    state,
    country,
    lat,
    lng
  }
}`;
export const sanityArticleMetadataProjection = `"articleMetadata": select(_type == "article" => {
  articleAuthor->{
    _id,
    _type,
    title,
    "slug": slug.current,
    picture
  },
  "articleCategory": o::refSummary(articleCategory),
  "industryCategory": o::refSummary(industryCategory),
  "relatedArticles": o::refSummaries(relatedArticles),
  excludeFromBlog,
  postDate,
  "dateUpdated": _updatedAt,
  articleLegacyImage
})`;
export const sanityEventMetadataProjection = `"eventMetadata": select(_type == "event" => {
  city,
  location,
  startDate,
  endDate,
  hideTime,
  hideEndDate,
  timezoneLabel,
  boothNumber,
  moreInfo
})`;
export const sanityWebinarMetadataProjection = `"webinarMetadata": select(_type == "webinar" => {
  webinarDate,
  linkText,
  "watchMedia": o::media(watchMedia),
  watchText
})`;
export const sanityCaseStudyMetadataProjection = `"caseStudyMetadata": select(_type == "caseStudy" => {
  introHeadline,
  introText,
  details[],
  "industries": o::refSummaries(industries),
  "hardwareProducts": o::refSummaries(hardwareProducts),
  locations[]{
    _key,
    _type,
    city,
    state,
    country,
    lat,
    lng
  },
  showRelatedCaseStudies,
  "relatedCaseStudies": o::refSummaries(relatedCaseStudies),
  showSocialShare,
  postDate,
  "dateUpdated": _updatedAt
})`;
export const sanityHardwareMetadataProjection = `"hardwareMetadata": select(_type == "hardware" => {
  productCaption,
  productImage,
  productHighlights,
  productAddOns[]{
    _key,
    _type,
    image,
    name,
    text
  },
  productGeneration->{_id, _type, title, slug}
})`;
export const sanitySoftwareMetadataProjection = `"softwareMetadata": select(_type == "software" => {
  productCaption,
  productImage,
  productIsFree
})`;

/**
 * `pageBuilderProjection` is the seam for embedding a page-builder projection
 * (full or page-scoped, see {@link buildSanityPageBuilderSectionProjection})
 * into the route document. Web loads sections separately, so the exported
 * route projection leaves it out.
 */
function createSanityRouteDocumentProjection(pageBuilderProjection = "") {
  return `{
  _id,
  _type,
  title,
  "uri": uri.current,
  "href": select(
    uri.current == "/" => "/",
    string::startsWith(uri.current, "/") => uri.current,
    defined(uri.current) => "/" + uri.current,
    null
  ),
  ${sanitySeoMetadataProjection},
  ${sanityHeroProjection},
  ${pageBuilderProjection ? `${pageBuilderProjection},` : ""}
  ${sanityArticleMetadataProjection},
  ${sanityEventMetadataProjection},
  ${sanityWebinarMetadataProjection},
  ${sanityCaseStudyMetadataProjection},
  ${sanityHardwareMetadataProjection},
  ${sanitySoftwareMetadataProjection},
  featuredArticle->${sanityArticleCardProjection},
  highlightedArticlesHeadline,
  highlightedArticles[]->${sanityArticleCardProjection},
  navTheme,
  pagePattern,
  footerPattern,
  pageMode,
  designVersion,
  customLogo,
  isHighlighted,
  hideFooter,
  backButton,
  pageNavAdditionalLinks[]{
    _type == "navigationLink" => ${sanityNavigationLinkProjection},
    _type == "navigationAdditionalDropdown" => ${sanityNavigationAdditionalDropdownProjection}
  },
  "supportLink": o::link(supportLink),
  supportLinkVariant,
  subNavLinks[] ${sanityNavigationLinkProjection},
  subNavMode,
  popup,
  enableImageParallax
}`;
}
export const sanityRouteDocumentWithoutPageBuilderProjection = createSanityRouteDocumentProjection();

/**
 * The route document by `_id`, for use after {@link sanityRouteTableQuery}
 * resolves a URL to an id.
 *
 * Same projection as the by-uri query so the rendered result is identical, and
 * the type filter is retained so an id that stops being routable cannot
 * resolve. Keying on `_id` matters for caching: document ids are a bounded key
 * space (290 today), whereas the requested URL is not.
 */
export const sanityRouteDocumentByIdQuery = withSanityGroqFunctions(`*[_id == $docId && _type in [${sanityRouteDocumentTypeFilter}]][0]${sanityRouteDocumentWithoutPageBuilderProjection}`);